import {
  Activity,
  AlertTriangle,
  ArrowDownToLine,
  BookOpenCheck,
  Box,
  Braces,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  CloudCog,
  Code2,
  Download,
  Egg,
  FileCog,
  Gauge,
  Hammer,
  History,
  Layers3,
  LockKeyhole,
  LoaderCircle,
  MapPinned,
  Menu,
  PackageOpen,
  PawPrint,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Rocket,
  RotateCw,
  Save,
  Search,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  SunMoon,
  TimerReset,
  Trash2,
  TrendingUp,
  Upload,
  Wheat,
  X,
  Zap
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type {
  ArkItemOptionDto,
  ArkServer,
  BeaconSetSummary,
  ConfigFileName,
  ConfigProfile,
  ConfigSetCreatureAdjustmentDto,
  ClusterPowerAction,
  ClusterPowerResult,
  CraftingIngredientDto,
  CraftingRecipeDto,
  CreatureAdjustmentDto,
  DeploymentRecord,
  DeploymentSchedule,
  GeneralSettingChangeDto,
  GeneralSettingDto,
  HotZoneConfigDto,
  IniEntryDto,
  LootDropDto,
  SpawnContainerDto
} from "../shared/types";
import { ApiError, api, type Bootstrap } from "./api";

const iconMap: Record<string, typeof Activity> = {
  Activity,
  BookOpenCheck,
  Braces,
  Egg,
  Gauge,
  Hammer,
  Layers3,
  MapPinned,
  PackageOpen,
  PawPrint,
  ShieldCheck,
  SlidersHorizontal,
  SunMoon,
  TimerReset,
  TrendingUp,
  Wheat
};

type Page = "dashboard" | "servers" | "automation" | "deployments" | "settings" | string;

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function applyFriendlyValue(
  raw: string,
  kind: NonNullable<IniEntryDto["friendlyForm"]>["kind"],
  fields: NonNullable<IniEntryDto["friendlyForm"]>["fields"]
): string {
  const field = (id: string) => fields.find((item) => item.id === id)?.value;
  const arkBoolean = (value: unknown) => (value ? "True" : "False");
  if (kind === "scalar") {
    const next = field("value");
    return typeof next === "boolean" ? arkBoolean(next) : String(next ?? "");
  }
  if (kind === "stack") {
    return raw
      .replace(/MaxItemQuantity=[^,)]+/i, `MaxItemQuantity=${field("amount")}`)
      .replace(/bIgnoreMultiplier=(true|false)/i, `bIgnoreMultiplier=${arkBoolean(field("ignoreMultiplier"))}`);
  }
  if (kind === "multiplier") {
    return raw.replace(/Multiplier=[^,)]+/i, `Multiplier=${field("multiplier")}`);
  }
  if (kind === "auto-unlock") {
    return raw.replace(/LevelToAutoUnlock=[^,)]+/i, `LevelToAutoUnlock=${field("level")}`);
  }
  let next = raw;
  if (field("hidden") !== undefined) {
    next = next.replace(/EngramHidden=(true|false)/i, `EngramHidden=${arkBoolean(field("hidden"))}`);
  }
  if (field("points") !== undefined) {
    next = next.replace(/EngramPointsCost=[^,)]+/i, `EngramPointsCost=${field("points")}`);
  }
  if (field("level") !== undefined) {
    next = next.replace(/EngramLevelRequirement=[^,)]+/i, `EngramLevelRequirement=${field("level")}`);
  }
  return next;
}

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(" ");
}

function StatusPill({ status }: { status?: string }) {
  const good = ["started", "succeeded", "active", "ok"].includes(status ?? "");
  const bad = ["failed", "unavailable", "error"].includes(status ?? "");
  return (
    <span className={cx("status-pill", good && "good", bad && "bad")}>
      <span className="status-dot" />
      {status ?? "not checked"}
    </span>
  );
}

function Toast({
  message,
  tone,
  onClose
}: {
  message: string;
  tone: "success" | "error";
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4_000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={cx("toast", tone)}>
      {tone === "success" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
      <span>{message}</span>
      <button onClick={onClose} aria-label="Close notification">
        <X size={16} />
      </button>
    </div>
  );
}

function LoginScreen({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  return (
    <main className="login-screen">
      <form
        className="login-card"
        onSubmit={async (event) => {
          event.preventDefault();
          setSubmitting(true);
          setError("");
          try {
            await api.login(password);
            await onSuccess();
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <div className="brand login-brand">
          <span className="brand-mark"><Zap /></span>
          <div><strong>WAZY</strong><small>CONTROL</small></div>
        </div>
        <div className="login-lock"><LockKeyhole /></div>
        <small>PRIVATE CLUSTER CONTROL</small>
        <h1>Sign in to continue</h1>
        <p>Server passwords, Nitrado controls and deployment schedules stay behind this login.</p>
        <label>
          <span>Application password</span>
          <input
            autoFocus
            autoComplete="current-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {error && <div className="login-error"><AlertTriangle size={16} />{error}</div>}
        <button className="button primary" disabled={submitting || !password}>
          {submitting ? <LoaderCircle className="spin" /> : <ShieldCheck />}
          Unlock control center
        </button>
      </form>
    </main>
  );
}

function Dashboard({
  data,
  profile,
  onNavigate,
  onDeploy
}: {
  data: Bootstrap;
  profile: ConfigProfile;
  onNavigate: (page: Page) => void;
  onDeploy: () => void;
}) {
  const totalEntries = data.files.reduce((sum, file) => sum + file.entries, 0);
  const enabledServers = data.state.servers.filter((server) => server.enabled);
  const nextSchedule = data.state.schedules
    .filter((item) => item.enabled)
    .sort((a, b) => new Date(a.wipeAt).getTime() - new Date(b.wipeAt).getTime())[0];
  const recentDeployment = data.state.deployments[0];
  return (
    <main className="page dashboard">
      <section className="hero">
        <div>
          <div className="eyebrow">
            <Zap size={14} /> ARK: Survival Evolved control center
          </div>
          <h1>Wipe configs, under control.</h1>
          <p>
            Your full Wazy project is indexed and ready for staged deployment. Edit any INI value,
            preview the exact output, then roll it across the cluster.
          </p>
          <div className="hero-actions">
            <button className="button primary" onClick={onDeploy}>
              <Rocket size={18} /> Deploy {profile.name}
            </button>
            <button className="button secondary" onClick={() => onNavigate("automation")}>
              <CalendarClock size={18} /> Schedule a wipe
            </button>
          </div>
        </div>
        <div className="hero-signal">
          <div className="signal-rings">
            <CloudCog size={34} />
          </div>
          <span>Deployment system</span>
          <strong>{data.integrations.nitrado.configured ? "Nitrado connected" : "Token required"}</strong>
          <StatusPill status={data.integrations.nitrado.configured ? "active" : "setup needed"} />
        </div>
      </section>

      <section className="metric-grid">
        <button className="metric-card" onClick={() => onNavigate("servers")}>
          <span className="metric-icon cyan"><Server /></span>
          <span><strong>{enabledServers.length}</strong><small>Cluster servers</small></span>
          <ChevronRight size={18} />
        </button>
        <button className="metric-card" onClick={() => onNavigate("custom")}>
          <span className="metric-icon violet"><Code2 /></span>
          <span><strong>{totalEntries.toLocaleString()}</strong><small>Indexed INI entries</small></span>
          <ChevronRight size={18} />
        </button>
        <button className="metric-card" onClick={() => onNavigate("loot")}>
          <span className="metric-icon amber"><PackageOpen /></span>
          <span><strong>{data.files.find((file) => file.file === "Game.ini")?.categories.loot ?? 0}</strong><small>Loot drop overrides</small></span>
          <ChevronRight size={18} />
        </button>
        <button className="metric-card" onClick={() => onNavigate("automation")}>
          <span className="metric-icon rose"><Clock3 /></span>
          <span><strong>{data.state.profiles.length}</strong><small>Timed config stages</small></span>
          <ChevronRight size={18} />
        </button>
      </section>

      <section className="dashboard-grid">
        <div className="panel timeline-panel">
          <div className="panel-heading">
            <div><small>WIPE AUTOMATION</small><h2>Release timeline</h2></div>
            <button className="text-button" onClick={() => onNavigate("automation")}>Manage schedule <ChevronRight size={15} /></button>
          </div>
          <div className="timeline">
            {data.state.profiles.map((item, index) => (
              <div className={cx("timeline-step", item.id === profile.id && "active")} key={item.id}>
                <div className="timeline-rail">
                  <span style={{ borderColor: item.color, background: item.id === profile.id ? item.color : undefined }}>
                    {item.id === profile.id ? <Check size={13} /> : index + 1}
                  </span>
                  {index < data.state.profiles.length - 1 && <i />}
                </div>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.offsetHours === 0 ? "At wipe" : `+${item.offsetHours} hours`}</span>
                  <p>{item.description}</p>
                  <div className="chip-row">
                    {item.activeConfigSets.map((set) => <em key={set}>{set}</em>)}
                    {!item.activeConfigSets.length && <em className="muted">No temporary bans</em>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="stack">
          <div className="panel compact-panel">
            <div className="panel-heading">
              <div><small>NEXT RUN</small><h2>{nextSchedule?.name ?? "No active schedule"}</h2></div>
              <CalendarClock size={22} />
            </div>
            {nextSchedule ? (
              <div className="schedule-summary">
                <strong>{new Date(nextSchedule.wipeAt).toLocaleString()}</strong>
                <span>{nextSchedule.serverIds.length} servers · {nextSchedule.timezone}</span>
              </div>
            ) : (
              <p className="empty-copy">Create one schedule and every 24h/48h/72h stage can run without manual work.</p>
            )}
          </div>
          <div className="panel compact-panel">
            <div className="panel-heading">
              <div><small>LAST DEPLOYMENT</small><h2>{recentDeployment ? profileName(data, recentDeployment.profileId) : "No deployments yet"}</h2></div>
              <History size={22} />
            </div>
            {recentDeployment ? (
              <div className="schedule-summary">
                <StatusPill status={recentDeployment.status} />
                <span>{new Date(recentDeployment.requestedAt).toLocaleString()} · {recentDeployment.serverIds.length} servers</span>
              </div>
            ) : <p className="empty-copy">Start with a dry run to validate the complete pipeline.</p>}
          </div>
          <div className={cx("notice", data.validation.some((issue) => issue.level === "error") ? "danger" : "info")}>
            {data.validation.length ? <AlertTriangle size={20} /> : <ShieldCheck size={20} />}
            <div>
              <strong>{data.validation.length ? `${data.validation.length} config notices` : "Config files validated"}</strong>
              <span>{data.validation[0]?.message ?? "Game.ini and GameUserSettings.ini have the expected sections."}</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function profileName(data: Bootstrap, profileId: string) {
  return data.state.profiles.find((item) => item.id === profileId)?.name ?? profileId;
}

function SpawnEditor({
  profile,
  notify
}: {
  profile: ConfigProfile;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.spawnContainers>>>();
  const [selected, setSelected] = useState<SpawnContainerDto>();
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadSpawns = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.spawnContainers(profile.id, { q: debouncedQuery, page }));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, debouncedQuery, page, notify]);

  useEffect(() => { void loadSpawns(); }, [loadSpawns]);
  useEffect(() => setPage(1), [profile.id, debouncedQuery]);

  return (
    <main className="page config-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><MapPinned size={24} /></span>
          <div>
            <small>VISUAL SPAWN EDITOR</small>
            <h1>Creature Spawns</h1>
            <p>Choose spawn areas and creatures by name. ARK container syntax is generated automatically.</p>
          </div>
        </div>
        <div className="page-actions">
          <a className="button secondary" href={`/api/config/${profile.id}/Game.ini/download`}>
            <Download size={17} /> Export Game.ini
          </a>
          <button className="button primary" onClick={() => setCreating(true)}><Plus /> New spawn area</button>
        </div>
      </section>
      <section className="panel crafting-browser">
        <div className="config-toolbar">
          <div className="crafting-help"><MapPinned size={17} /><span>Spawn sets, chances, levels and population limits</span></div>
          <label className="search-box">
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Fertile Land, Raptor, Aberration…" />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>
        <div className="table-meta">
          <span><strong>{result?.total.toLocaleString() ?? "—"}</strong> spawn containers</span>
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
        </div>
        <div className="recipe-list">
          {loading ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading spawn containers…</div>
          ) : result?.items.length ? (
            result.items.map((container) => {
              const creatureNames = [...new Set(
                container.entries.flatMap((entry) => entry.creatures.map((creature) => creature.name))
              )];
              const creatureCount = container.entries.reduce((sum, entry) => sum + entry.creatures.length, 0);
              return (
                <button className="recipe-row spawn-row" key={container.id} onClick={() => setSelected(container)}>
                  <span className={cx("recipe-icon", "spawn", container.mode)}><MapPinned /></span>
                  <span className="recipe-summary">
                    <strong>{container.name}</strong>
                    <span>
                      {creatureNames.slice(0, 5).map((name) => <em key={name}>{name}</em>)}
                      {creatureNames.length > 5 && <i>+{creatureNames.length - 5} more</i>}
                    </span>
                  </span>
                  <span className={cx("mini-badge", container.overridden && "changed")}>
                    {container.managedByConfigSet ? "Managed Hot Zone · " : ""}{container.entries.length} sets · {creatureCount} spawns
                  </span>
                  <ChevronRight size={19} />
                </button>
              );
            })
          ) : (
            <div className="empty-state"><MapPinned /><strong>No matching spawn area</strong><span>Search by area, set or creature name.</span></div>
          )}
        </div>
        <div className="pagination">
          <button disabled={(result?.page ?? 1) <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /> Previous</button>
          <span>Page <strong>{result?.page ?? 1}</strong> of <strong>{result?.pages ?? 1}</strong></span>
          <button disabled={(result?.page ?? 1) >= (result?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={17} /></button>
        </div>
      </section>
      {selected && (
        <SpawnModal
          summary={selected}
          profile={profile}
          onClose={() => setSelected(undefined)}
          onSaved={async () => {
            setSelected(undefined);
            await loadSpawns();
          }}
          notify={notify}
        />
      )}
      {creating && (
        <SpawnCreateModal
          profile={profile}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await loadSpawns();
          }}
          notify={notify}
        />
      )}
    </main>
  );
}

function SpawnCreateModal({
  profile,
  onClose,
  onCreated,
  notify
}: {
  profile: ConfigProfile;
  onClose: () => void;
  onCreated: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof api.spawnCatalog>>>();
  const [spawnPoint, setSpawnPoint] = useState("");
  const [creatureClass, setCreatureClass] = useState("");
  const [mode, setMode] = useState<"add" | "replace">("add");
  const [setName, setSetName] = useState("New Spawn Set");
  const [weight, setWeight] = useState(1);
  const [chance, setChance] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.spawnCatalog()
      .then(setCatalog)
      .catch((error) => notify(error instanceof Error ? error.message : String(error), "error"));
  }, [notify]);
  const selectedPoint = catalog?.spawnPoints.find((point) => point.className === spawnPoint);
  const selectedCreature = catalog?.creatures.find((creature) => creature.className === creatureClass);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal spawn-create-modal">
        <div className="modal-heading">
          <div><small>NEW SPAWN CONFIG · {profile.name}</small><h2>Add spawn area</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body">
          {!catalog ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading spawn catalog…</div>
          ) : (
            <div className="form-grid">
              <label className="full"><span>Spawn area</span><select value={spawnPoint} onChange={(event) => setSpawnPoint(event.target.value)}><option value="">Choose spawn area…</option>{catalog.spawnPoints.map((point) => <option value={point.className} key={point.className}>{point.name} · {point.contentPackName}</option>)}</select></label>
              <label><span>Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as "add" | "replace")}><option value="add">Add to original creatures</option><option value="replace">Replace original creatures</option></select></label>
              <label><span>Spawn set name</span><input value={setName} onChange={(event) => setSetName(event.target.value)} /></label>
              <label className="full"><span>First creature</span><select value={creatureClass} onChange={(event) => setCreatureClass(event.target.value)}><option value="">Choose creature…</option>{catalog.creatures.map((creature) => <option value={creature.className} key={creature.className}>{creature.name} · {creature.contentPackName}</option>)}</select></label>
              <label><span>Entry weight</span><input type="number" min="0" step="0.01" value={weight} onChange={(event) => setWeight(Number(event.target.value))} /></label>
              <label><span>Spawn chance</span><input type="number" min="0" max="1" step="0.01" value={chance} onChange={(event) => setChance(Number(event.target.value))} /></label>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={!selectedPoint || !selectedCreature || !setName.trim() || saving} onClick={async () => {
            if (!selectedPoint || !selectedCreature) return;
            setSaving(true);
            try {
              await api.createSpawnContainer(profile.id, {
                id: "",
                key: mode === "add" ? "ConfigAddNPCSpawnEntriesContainer" : "ConfigOverrideNPCSpawnEntriesContainer",
                mode,
                className: selectedPoint.className,
                name: selectedPoint.name,
                entries: [{
                  name: setName,
                  weight,
                  creatures: [{
                    className: selectedCreature.className,
                    name: selectedCreature.name,
                    chance,
                    offsetX: 0,
                    offsetY: 0,
                    offsetZ: 0,
                    hasOffset: false
                  }]
                }],
                limits: [],
                overridden: true
              });
              notify(`${selectedPoint.name} spawn area created.`);
              await onCreated();
            } catch (error) {
              notify(error instanceof Error ? error.message : String(error), "error");
            } finally {
              setSaving(false);
            }
          }}>{saving ? <LoaderCircle className="spin" /> : <Plus />} Create spawn area</button>
        </div>
      </div>
    </div>
  );
}

function SpawnModal({
  summary,
  profile,
  onClose,
  onSaved,
  notify
}: {
  summary: SpawnContainerDto;
  profile: ConfigProfile;
  onClose: () => void;
  onSaved: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [container, setContainer] = useState<SpawnContainerDto>();
  const [creatures, setCreatures] = useState<ArkItemOptionDto[]>([]);
  const [pendingCreatures, setPendingCreatures] = useState<Record<number, string>>({});
  const [pendingLimit, setPendingLimit] = useState("");
  const [saving, setSaving] = useState(false);
  const creatureByClass = useMemo(
    () => new Map(creatures.map((creature) => [creature.className, creature])),
    [creatures]
  );

  useEffect(() => {
    void Promise.all([api.spawnContainer(profile.id, summary.id), api.spawnCatalog()])
      .then(([loadedContainer, catalog]) => {
        setContainer(loadedContainer);
        setCreatures(catalog.creatures);
      })
      .catch((error) => notify(error instanceof Error ? error.message : String(error), "error"));
  }, [profile.id, summary.id, notify]);

  function updateSpawnSet(
    setIndex: number,
    patch: Partial<SpawnContainerDto["entries"][number]>
  ) {
    setContainer((current) => current
      ? {
          ...current,
          entries: current.entries.map((entry, index) =>
            index === setIndex ? { ...entry, ...patch } : entry
          )
        }
      : current);
  }

  function updateSpawnCreature(
    setIndex: number,
    creatureIndex: number,
    patch: Partial<SpawnContainerDto["entries"][number]["creatures"][number]>
  ) {
    setContainer((current) => current
      ? {
          ...current,
          entries: current.entries.map((entry, index) =>
            index === setIndex
              ? {
                  ...entry,
                  creatures: entry.creatures.map((creature, target) =>
                    target === creatureIndex ? { ...creature, ...patch } : creature
                  )
                }
              : entry
          )
        }
      : current);
  }

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal spawn-modal">
        <div className="modal-heading">
          <div><small>{summary.mode === "replace" ? "REPLACE SPAWN AREA" : "ADD TO SPAWN AREA"} · {profile.name}</small><h2>{summary.name}</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body spawn-modal-body">
          {!container || !creatures.length ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading spawn sets…</div>
          ) : (
            <>
              <div className="recipe-note"><MapPinned /><div><strong>{container.managedByConfigSet ? `Managed by ${container.managedByConfigSet}` : container.mode === "replace" ? "Replaces the original area" : "Adds creatures to the original area"}</strong><span>{container.managedByConfigSet ? "Inspect the generated result here. Edit shares, levels and creatures from Config Sets so active bans remain authoritative." : "Edit familiar names below; the spawn container class is preserved automatically."}</span></div></div>
              <div className="spawn-set-list">
                {container.entries.map((entry, setIndex) => (
                  <details className="spawn-set" open={setIndex === 0} key={`${entry.name}-${setIndex}`}>
                    <summary>
                      <span><strong>{entry.name}</strong><small>{entry.creatures.length} creature spawns</small></span>
                      <span>Weight {entry.weight}<ChevronRight /></span>
                    </summary>
                    <div className="spawn-set-fields">
                      <label><span>Spawn set name</span><input value={entry.name} onChange={(event) => updateSpawnSet(setIndex, { name: event.target.value })} /></label>
                      <label><span>Entry weight</span><input type="number" min="0" step="0.001" value={entry.weight} onChange={(event) => updateSpawnSet(setIndex, { weight: Number(event.target.value) })} /></label>
                      <button className="button danger-outline" disabled={container.entries.length <= 1} onClick={() => setContainer({ ...container, entries: container.entries.filter((_, index) => index !== setIndex) })}><Trash2 /> Remove set</button>
                    </div>
                    <div className="spawn-creature-list">
                      {entry.creatures.map((creature, creatureIndex) => (
                        <article className="spawn-creature" key={`${creature.className}-${creatureIndex}`}>
                          <div className="spawn-creature-heading">
                            <span className="recipe-icon creature"><PawPrint /></span>
                            <span><strong>{creatureByClass.get(creature.className)?.name ?? creature.name}</strong><small>Spawn instance {creatureIndex + 1}</small></span>
                            <button className="icon-button danger" title="Remove creature" onClick={() => updateSpawnSet(setIndex, { creatures: entry.creatures.filter((_, index) => index !== creatureIndex) })}><Trash2 /></button>
                          </div>
                          <div className="spawn-creature-fields">
                            <label><span>Spawn chance</span><input type="number" min="0" max="1" step="0.01" value={creature.chance} onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { chance: Number(event.target.value) })} /></label>
                            <label><span>Minimum level</span><input type="number" min="0" value={creature.minLevel ?? ""} placeholder="Default" onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { minLevel: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
                            <label><span>Maximum level</span><input type="number" min="0" value={creature.maxLevel ?? ""} placeholder="Default" onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { maxLevel: event.target.value === "" ? undefined : Number(event.target.value) })} /></label>
                            <label className="spawn-offset-toggle"><input type="checkbox" checked={creature.hasOffset} onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { hasOffset: event.target.checked })} /><span>Custom position</span></label>
                            {creature.hasOffset && <>
                              <label><span>Offset X</span><input type="number" value={creature.offsetX} onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { offsetX: Number(event.target.value) })} /></label>
                              <label><span>Offset Y</span><input type="number" value={creature.offsetY} onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { offsetY: Number(event.target.value) })} /></label>
                              <label><span>Offset Z</span><input type="number" value={creature.offsetZ} onChange={(event) => updateSpawnCreature(setIndex, creatureIndex, { offsetZ: Number(event.target.value) })} /></label>
                            </>}
                          </div>
                        </article>
                      ))}
                    </div>
                    <div className="spawn-add-row">
                      <select value={pendingCreatures[setIndex] ?? ""} onChange={(event) => setPendingCreatures((current) => ({ ...current, [setIndex]: event.target.value }))}>
                        <option value="">Choose creature…</option>
                        {creatures.map((creature) => <option value={creature.className} key={creature.className}>{creature.name} · {creature.contentPackName}</option>)}
                      </select>
                      <button className="button secondary" disabled={!pendingCreatures[setIndex]} onClick={() => {
                        const selectedCreature = creatureByClass.get(pendingCreatures[setIndex]);
                        if (!selectedCreature) return;
                        updateSpawnSet(setIndex, {
                          creatures: [...entry.creatures, {
                            className: selectedCreature.className,
                            name: selectedCreature.name,
                            chance: 1,
                            offsetX: 0,
                            offsetY: 0,
                            offsetZ: 0,
                            hasOffset: false
                          }]
                        });
                        setPendingCreatures((current) => ({ ...current, [setIndex]: "" }));
                      }}><Plus /> Add creature</button>
                    </div>
                  </details>
                ))}
              </div>
              <button className="button secondary add-spawn-set" onClick={() => setContainer({
                ...container,
                entries: [...container.entries, {
                  name: `Spawn Set ${container.entries.length + 1}`,
                  weight: 1,
                  creatures: []
                }]
              })}><Plus /> Add spawn set</button>
              <section className="spawn-limits">
                <div className="spawn-section-heading"><div><small>POPULATION LIMITS</small><h3>Maximum share of this spawn area</h3></div><span>{container.limits.length} limits</span></div>
                <div className="spawn-limit-list">
                  {container.limits.map((limit, limitIndex) => (
                    <div className="spawn-limit" key={`${limit.className}-${limitIndex}`}>
                      <strong>{creatureByClass.get(limit.className)?.name ?? limit.name}</strong>
                      <label><span>Maximum percentage</span><input type="number" min="0" max="1" step="0.001" value={limit.maxPercentage} onChange={(event) => setContainer({
                        ...container,
                        limits: container.limits.map((item, index) => index === limitIndex ? { ...item, maxPercentage: Number(event.target.value) } : item)
                      })} /></label>
                      <button className="icon-button danger" onClick={() => setContainer({ ...container, limits: container.limits.filter((_, index) => index !== limitIndex) })}><Trash2 /></button>
                    </div>
                  ))}
                </div>
                <div className="spawn-add-row">
                  <select value={pendingLimit} onChange={(event) => setPendingLimit(event.target.value)}>
                    <option value="">Choose creature limit…</option>
                    {creatures.map((creature) => <option value={creature.className} key={creature.className}>{creature.name}</option>)}
                  </select>
                  <button className="button secondary" disabled={!pendingLimit} onClick={() => {
                    const selectedCreature = creatureByClass.get(pendingLimit);
                    if (!selectedCreature) return;
                    setContainer({
                      ...container,
                      limits: [...container.limits, {
                        className: selectedCreature.className,
                        name: selectedCreature.name,
                        maxPercentage: 0.1
                      }]
                    });
                    setPendingLimit("");
                  }}><Plus /> Add limit</button>
                </div>
              </section>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={!container || saving || Boolean(container?.managedByConfigSet)} onClick={async () => {
            if (!container) return;
            setSaving(true);
            try {
              await api.saveSpawnContainer(profile.id, summary.id, container);
              notify(`${container.name} spawn setup saved.`);
              await onSaved();
            } catch (error) {
              notify(error instanceof Error ? error.message : String(error), "error");
            } finally {
              setSaving(false);
            }
          }}>{saving ? <LoaderCircle className="spin" /> : <Save />} {container?.managedByConfigSet ? "Edit in Config Sets" : "Save spawns"}</button>
        </div>
      </div>
    </div>
  );
}

function CreatureEditor({
  profile,
  notify
}: {
  profile: ConfigProfile;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.creatures>>>();
  const [selected, setSelected] = useState<CreatureAdjustmentDto>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadCreatures = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.creatures(profile.id, { q: debouncedQuery, page }));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, debouncedQuery, page, notify]);

  useEffect(() => { void loadCreatures(); }, [loadCreatures]);
  useEffect(() => setPage(1), [profile.id, debouncedQuery]);

  return (
    <main className="page config-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><PawPrint size={24} /></span>
          <div>
            <small>COMPLETE CREATURE CATALOG</small>
            <h1>Creature Adjustments</h1>
            <p>One familiar in-game name per creature. Class names are generated silently in Game.ini.</p>
          </div>
        </div>
        <a className="button secondary" href={`/api/config/${profile.id}/Game.ini/download`}>
          <Download size={17} /> Export Game.ini
        </a>
      </section>
      <section className="panel crafting-browser">
        <div className="config-toolbar">
          <div className="crafting-help"><PawPrint size={17} /><span>Every official ASE creature and variant</span></div>
          <label className="search-box">
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Rex, Aberrant Spino, Megapithecus Hard…" />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>
        <div className="table-meta">
          <span><strong>{result?.total.toLocaleString() ?? "—"}</strong> creatures available</span>
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
        </div>
        <div className="creature-list-head">
          <span /><span>Creature</span><span>Wild damage</span><span>Wild resistance</span><span>Tamed damage</span><span>Tamed resistance</span><span /><span />
        </div>
        <div className="recipe-list creature-list">
          {loading ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading creature catalog…</div>
          ) : result?.items.length ? (
            result.items.map((creature) => (
              <button className="creature-row" key={creature.className} onClick={() => setSelected(creature)}>
                <span className="recipe-icon creature"><PawPrint /></span>
                <span className="creature-name">
                  <strong>{creature.name}</strong>
                  <small>{creature.contentPackName ?? "ARK: Survival Evolved"}</small>
                </span>
                <span>{creature.wildDamage}×</span>
                <span>{creature.wildResistance}×</span>
                <span>{creature.tamedDamage}×</span>
                <span>{creature.tamedResistance}×</span>
                <span className={cx("mini-badge", creature.overridden && "changed", creature.prohibitSpawning && "blocked")}>{creature.prohibitSpawning ? "No spawn" : creature.overridden ? "Changed" : creature.configured.length ? "Base" : "Default"}</span>
                <ChevronRight size={18} />
              </button>
            ))
          ) : (
            <div className="empty-state"><PawPrint /><strong>No matching creature</strong><span>Try its in-game name.</span></div>
          )}
        </div>
        <div className="pagination">
          <button disabled={(result?.page ?? 1) <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /> Previous</button>
          <span>Page <strong>{result?.page ?? 1}</strong> of <strong>{result?.pages ?? 1}</strong></span>
          <button disabled={(result?.page ?? 1) >= (result?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={17} /></button>
        </div>
      </section>
      {selected && (
        <CreatureModal
          creature={selected}
          profile={profile}
          onClose={() => setSelected(undefined)}
          onSaved={async () => {
            setSelected(undefined);
            await loadCreatures();
          }}
          notify={notify}
        />
      )}
    </main>
  );
}

function CreatureModal({
  creature,
  profile,
  onClose,
  onSaved,
  notify
}: {
  creature: CreatureAdjustmentDto;
  profile: ConfigProfile;
  onClose: () => void;
  onSaved: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [values, setValues] = useState({
    wildDamage: creature.wildDamage,
    wildResistance: creature.wildResistance,
    tamedDamage: creature.tamedDamage,
    tamedResistance: creature.tamedResistance,
    prohibitSpawning: creature.prohibitSpawning,
    preventTaming: creature.preventTaming,
    prohibitTransfer: creature.prohibitTransfer,
    prohibitBreeding: creature.prohibitBreeding
  });
  const [saving, setSaving] = useState(false);
  const fields = [
    { id: "wildDamage" as const, label: "Wild Damage", help: "Damage dealt by wild versions" },
    { id: "wildResistance" as const, label: "Wild Resistance", help: "Damage taken by wild versions" },
    { id: "tamedDamage" as const, label: "Tamed Damage", help: "Damage dealt after taming" },
    { id: "tamedResistance" as const, label: "Tamed Resistance", help: "Damage taken after taming" }
  ];

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal creature-modal">
        <div className="modal-heading">
          <div><small>CREATURE ADJUSTMENT · {profile.name}</small><h2>{creature.name}</h2><span>{creature.contentPackName}</span></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body">
          <div className="recipe-note"><PawPrint /><div><strong>Simple multipliers</strong><span>1.0 is the normal ARK value. The correct class string is written automatically.</span></div></div>
          <div className="creature-field-grid">
            {fields.map((field) => (
              <label className="creature-field" key={field.id}>
                <span><strong>{field.label}</strong><small>{field.help}</small></span>
                <div><input type="number" min="0" max="100000" step="0.01" value={values[field.id]} onChange={(event) => setValues((current) => ({ ...current, [field.id]: Number(event.target.value) }))} /><b>×</b></div>
              </label>
            ))}
          </div>
          <div className="creature-restriction-grid">
            {[
              { id: "prohibitSpawning" as const, label: "Disable spawning", help: "Remove this creature from natural spawns" },
              { id: "preventTaming" as const, label: "Prevent taming", help: "Players cannot tame this creature" },
              { id: "prohibitTransfer" as const, label: "Prevent transfer", help: "Block upload and cluster transfer" },
              { id: "prohibitBreeding" as const, label: "Prevent breeding", help: "Disable breeding for this creature" }
            ].map((restriction) => (
              <label key={restriction.id}>
                <input type="checkbox" checked={values[restriction.id]} onChange={(event) => setValues((current) => ({ ...current, [restriction.id]: event.target.checked }))} />
                <i />
                <span><strong>{restriction.label}</strong><small>{restriction.help}</small></span>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={saving || Object.values(values).filter((value): value is number => typeof value === "number").some((value) => !Number.isFinite(value) || value < 0)} onClick={async () => {
            setSaving(true);
            try {
              await api.saveCreature(profile.id, creature.className, values);
              notify(`${creature.name} multipliers saved.`);
              await onSaved();
            } catch (error) {
              notify(error instanceof Error ? error.message : String(error), "error");
            } finally {
              setSaving(false);
            }
          }}>{saving ? <LoaderCircle className="spin" /> : <Save />} Save creature</button>
        </div>
      </div>
    </div>
  );
}

function LootEditor({
  profile,
  notify
}: {
  profile: ConfigProfile;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.lootDrops>>>();
  const [selected, setSelected] = useState<LootDropDto>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadDrops = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.lootDrops(profile.id, { q: debouncedQuery, page }));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, debouncedQuery, page, notify]);

  useEffect(() => { void loadDrops(); }, [loadDrops]);
  useEffect(() => setPage(1), [profile.id, debouncedQuery]);

  return (
    <main className="page config-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><PackageOpen size={24} /></span>
          <div><small>VISUAL LOOT EDITOR</small><h1>Loot Drops</h1><p>Open a drop to inspect every item set, item, quantity and quality.</p></div>
        </div>
        <a className="button secondary" href={`/api/config/${profile.id}/Game.ini/download`}>
          <Download size={17} /> Export Game.ini
        </a>
      </section>
      <section className="panel crafting-browser">
        <div className="config-toolbar">
          <div className="crafting-help"><PackageOpen size={17} /><span>In-game names with Easy, Medium and Hard preserved</span></div>
          <label className="search-box">
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Gorilla Valguero, Red Drop, Tek Rifle…" />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>
        <div className="table-meta">
          <span><strong>{result?.total.toLocaleString() ?? "—"}</strong> configured loot drops</span>
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
        </div>
        <div className="recipe-list">
          {loading ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading loot drops…</div>
          ) : result?.items.length ? (
            result.items.map((drop) => (
              <button className="recipe-row loot-row" key={drop.id} onClick={() => setSelected(drop)}>
                <span className="recipe-icon loot"><PackageOpen /></span>
                <span className="recipe-summary">
                  <strong>{drop.name}</strong>
                  <span>
                    <em>{drop.itemSetCount} item {drop.itemSetCount === 1 ? "set" : "sets"}</em>
                    <em>{drop.itemEntryCount} item {drop.itemEntryCount === 1 ? "entry" : "entries"}</em>
                    <em>Drops {drop.minSets === drop.maxSets ? drop.minSets : `${drop.minSets}–${drop.maxSets}`} sets</em>
                  </span>
                </span>
                <span className={cx("mini-badge", drop.overridden && "changed")}>{drop.overridden ? "Changed" : "Base"}</span>
                <ChevronRight size={19} />
              </button>
            ))
          ) : (
            <div className="empty-state"><PackageOpen /><strong>No matching loot drops</strong><span>Search by drop, boss or item name.</span></div>
          )}
        </div>
        <div className="pagination">
          <button disabled={(result?.page ?? 1) <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /> Previous</button>
          <span>Page <strong>{result?.page ?? 1}</strong> of <strong>{result?.pages ?? 1}</strong></span>
          <button disabled={(result?.page ?? 1) >= (result?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={17} /></button>
        </div>
      </section>
      {selected && (
        <LootDropModal
          summary={selected}
          profile={profile}
          onClose={() => setSelected(undefined)}
          onSaved={async () => {
            setSelected(undefined);
            await loadDrops();
          }}
          notify={notify}
        />
      )}
    </main>
  );
}

function LootDropModal({
  summary,
  profile,
  onClose,
  onSaved,
  notify
}: {
  summary: LootDropDto;
  profile: ConfigProfile;
  onClose: () => void;
  onSaved: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [drop, setDrop] = useState<LootDropDto>();
  const [items, setItems] = useState<ArkItemOptionDto[]>([]);
  const [pendingItems, setPendingItems] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void Promise.all([api.lootDrop(profile.id, summary.id), api.lootItems()])
      .then(([loadedDrop, loadedItems]) => {
        setDrop(loadedDrop);
        setItems(loadedItems);
      })
      .catch((error) => notify(error instanceof Error ? error.message : String(error), "error"));
  }, [profile.id, summary.id, notify]);

  const itemByClass = useMemo(
    () => new Map(items.map((item) => [item.className, item])),
    [items]
  );

  function updateSet(setIndex: number, patch: Partial<LootDropDto["itemSets"][number]>) {
    setDrop((current) => current
      ? {
          ...current,
          itemSets: current.itemSets.map((set, index) =>
            index === setIndex ? { ...set, ...patch } : set
          )
        }
      : current);
  }

  function updateEntry(
    setIndex: number,
    entryIndex: number,
    patch: Partial<LootDropDto["itemSets"][number]["entries"][number]>
  ) {
    setDrop((current) => current
      ? {
          ...current,
          itemSets: current.itemSets.map((set, index) =>
            index === setIndex
              ? {
                  ...set,
                  entries: set.entries.map((entry, target) =>
                    target === entryIndex ? { ...entry, ...patch } : entry
                  )
                }
              : set
          )
        }
      : current);
  }

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal loot-modal">
        <div className="modal-heading">
          <div><small>EDIT LOOT DROP · {profile.name}</small><h2>{summary.name}</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body loot-modal-body">
          {!drop || !items.length ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Parsing item sets…</div>
          ) : (
            <>
              <div className="loot-global-editor">
                <label><span>Minimum sets</span><input type="number" min="0" value={drop.minSets} onChange={(event) => setDrop({ ...drop, minSets: Number(event.target.value) })} /></label>
                <label><span>Maximum sets</span><input type="number" min="0" value={drop.maxSets} onChange={(event) => setDrop({ ...drop, maxSets: Number(event.target.value) })} /></label>
                <label><span>Selection power</span><input type="number" min="0" step="0.1" value={drop.numSetsPower} onChange={(event) => setDrop({ ...drop, numSetsPower: Number(event.target.value) })} /></label>
                <label className="loot-checkbox"><input type="checkbox" checked={drop.randomWithoutReplacement} onChange={(event) => setDrop({ ...drop, randomWithoutReplacement: event.target.checked })} /><span><strong>No repeated sets</strong><small>Each set can be selected once</small></span></label>
              </div>
              <div className="loot-set-list">
                {drop.itemSets.map((set, setIndex) => (
                  <details className="loot-set" open={setIndex === 0} key={`${set.name}-${setIndex}`}>
                    <summary>
                      <span><strong>{set.name}</strong><small>{set.entries.length} entries · gives {set.minItems === set.maxItems ? set.minItems : `${set.minItems}–${set.maxItems}`} items</small></span>
                      <span>Weight {set.weight}<ChevronRight /></span>
                    </summary>
                    <div className="loot-set-editor">
                      <label className="wide"><span>Item set name</span><input value={set.name} onChange={(event) => updateSet(setIndex, { name: event.target.value })} /></label>
                      <label><span>Minimum items</span><input type="number" min="0" value={set.minItems} onChange={(event) => updateSet(setIndex, { minItems: Number(event.target.value) })} /></label>
                      <label><span>Maximum items</span><input type="number" min="0" value={set.maxItems} onChange={(event) => updateSet(setIndex, { maxItems: Number(event.target.value) })} /></label>
                      <label><span>Set weight</span><input type="number" min="0" value={set.weight} onChange={(event) => updateSet(setIndex, { weight: Number(event.target.value) })} /></label>
                      <label><span>Selection power</span><input type="number" min="0" step="0.1" value={set.numItemsPower} onChange={(event) => updateSet(setIndex, { numItemsPower: Number(event.target.value) })} /></label>
                      <label className="loot-checkbox wide"><input type="checkbox" checked={set.randomWithoutReplacement} onChange={(event) => updateSet(setIndex, { randomWithoutReplacement: event.target.checked })} /><span><strong>No repeated items</strong><small>Each entry can be selected once</small></span></label>
                    </div>
                    <div className="loot-entry-edit-list">
                      {set.entries.map((entry, entryIndex) => (
                        <div className="loot-entry-editor" key={`${entry.name}-${entryIndex}`}>
                          <div className="loot-entry-title">
                            <span><strong>{entry.classNames.map((className) => itemByClass.get(className)?.name ?? className).join(" or ")}</strong><small>{entry.classNames.length > 1 ? `${entry.classNames.length} possible items` : "Loot item"}</small></span>
                            <button className="icon-button danger" title="Remove item" onClick={() => updateSet(setIndex, { entries: set.entries.filter((_, index) => index !== entryIndex) })}><Trash2 /></button>
                          </div>
                          <div className="loot-item-chips">
                            {entry.classNames.slice(0, 12).map((className, classIndex) => (
                              <span key={`${className}-${classIndex}`}>
                                {itemByClass.get(className)?.name ?? entry.itemNames[classIndex] ?? "Unknown item"}
                                {entry.classNames.length > 1 && (
                                  <button title="Remove possible item" onClick={() => updateEntry(setIndex, entryIndex, {
                                    classNames: entry.classNames.filter((_, index) => index !== classIndex),
                                    itemNames: entry.itemNames.filter((_, index) => index !== classIndex),
                                    itemWeights: entry.itemWeights.filter((_, index) => index !== classIndex)
                                  })}><X /></button>
                                )}
                              </span>
                            ))}
                            {entry.classNames.length > 12 && <span>+{entry.classNames.length - 12} more possible items</span>}
                          </div>
                          <div className="loot-entry-fields">
                            <label><span>Min quantity</span><input type="number" min="0" value={entry.minQuantity} onChange={(event) => updateEntry(setIndex, entryIndex, { minQuantity: Number(event.target.value) })} /></label>
                            <label><span>Max quantity</span><input type="number" min="0" value={entry.maxQuantity} onChange={(event) => updateEntry(setIndex, entryIndex, { maxQuantity: Number(event.target.value) })} /></label>
                            <label><span>Min quality</span><input type="number" min="0" step="0.1" value={entry.minQuality} onChange={(event) => updateEntry(setIndex, entryIndex, { minQuality: Number(event.target.value) })} /></label>
                            <label><span>Max quality</span><input type="number" min="0" step="0.1" value={entry.maxQuality} onChange={(event) => updateEntry(setIndex, entryIndex, { maxQuality: Number(event.target.value) })} /></label>
                            <label><span>Entry weight</span><input type="number" min="0" value={entry.weight} onChange={(event) => updateEntry(setIndex, entryIndex, { weight: Number(event.target.value) })} /></label>
                            <label><span>Blueprint chance</span><input type="number" min="0" max="1" step="0.01" value={entry.blueprintChance} onChange={(event) => updateEntry(setIndex, entryIndex, { blueprintChance: Number(event.target.value) })} /></label>
                          </div>
                          <div className="loot-entry-toggles">
                            <label><input type="checkbox" checked={entry.forceBlueprint} onChange={(event) => updateEntry(setIndex, entryIndex, { forceBlueprint: event.target.checked })} /><span>Always blueprint</span></label>
                            <label><input type="checkbox" checked={entry.applyQuantityToSingleItem} onChange={(event) => updateEntry(setIndex, entryIndex, { applyQuantityToSingleItem: event.target.checked })} /><span>Apply quantity to one item</span></label>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="loot-set-actions">
                      <select value={pendingItems[setIndex] ?? ""} onChange={(event) => setPendingItems((current) => ({ ...current, [setIndex]: event.target.value }))}>
                        <option value="">Choose an item…</option>
                        {items.map((item) => <option value={item.className} key={item.className}>{item.name} · {item.contentPackName}</option>)}
                      </select>
                      <button className="button secondary" disabled={!pendingItems[setIndex]} onClick={() => {
                        const option = itemByClass.get(pendingItems[setIndex]);
                        if (!option) return;
                        updateSet(setIndex, {
                          entries: [...set.entries, {
                            name: option.name,
                            classNames: [option.className],
                            itemNames: [option.name],
                            itemWeights: [50],
                            weight: 100,
                            minQuantity: 1,
                            maxQuantity: 1,
                            minQuality: 0,
                            maxQuality: 0,
                            blueprintChance: 0,
                            forceBlueprint: false,
                            applyQuantityToSingleItem: true
                          }]
                        });
                        setPendingItems((current) => ({ ...current, [setIndex]: "" }));
                      }}><Plus /> Add item</button>
                      <button className="button danger-outline" disabled={drop.itemSets.length <= 1} onClick={() => setDrop({ ...drop, itemSets: drop.itemSets.filter((_, index) => index !== setIndex) })}><Trash2 /> Remove set</button>
                    </div>
                  </details>
                ))}
              </div>
              <button className="button secondary add-loot-set" onClick={() => setDrop({
                ...drop,
                itemSets: [...drop.itemSets, {
                  name: `Item Set ${drop.itemSets.length + 1}`,
                  minItems: 1,
                  maxItems: 1,
                  numItemsPower: 1,
                  weight: 500,
                  randomWithoutReplacement: true,
                  entries: []
                }]
              })}><Plus /> Add item set</button>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={!drop || saving} onClick={async () => {
            if (!drop) return;
            setSaving(true);
            try {
              await api.saveLootDrop(profile.id, summary.id, drop);
              notify(`${drop.name} loot saved.`);
              await onSaved();
            } catch (error) {
              notify(error instanceof Error ? error.message : String(error), "error");
            } finally {
              setSaving(false);
            }
          }}>{saving ? <LoaderCircle className="spin" /> : <Save />} Save loot drop</button>
        </div>
      </div>
    </div>
  );
}

function CraftingEditor({
  profile,
  notify,
  reload
}: {
  profile: ConfigProfile;
  notify: (message: string, tone?: "success" | "error") => void;
  reload: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.craftingRecipes>>>();
  const [editing, setEditing] = useState<CraftingRecipeDto>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.craftingRecipes(profile.id, { q: debouncedQuery, page }));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, debouncedQuery, page, notify]);

  useEffect(() => { void loadRecipes(); }, [loadRecipes]);
  useEffect(() => setPage(1), [profile.id, debouncedQuery]);

  return (
    <main className="page config-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><Hammer size={24} /></span>
          <div>
            <small>RECIPE EDITOR</small>
            <h1>Crafting Costs</h1>
            <p>Choose an item, then build its recipe from familiar ARK resources.</p>
          </div>
        </div>
        <a className="button secondary" href={`/api/config/${profile.id}/Game.ini/download`}>
          <Download size={17} /> Export Game.ini
        </a>
      </section>

      <section className="panel crafting-browser">
        <div className="config-toolbar">
          <div className="crafting-help">
            <Hammer size={17} />
            <span>Item paths are generated automatically</span>
          </div>
          <label className="search-box">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Smithy, Flamethrower, Polymer…"
            />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>
        <div className="table-meta">
          <span><strong>{result?.total.toLocaleString() ?? "—"}</strong> item recipes</span>
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
        </div>
        <div className="recipe-list">
          {loading ? (
            <div className="table-loading"><LoaderCircle className="spin" /> Loading recipes…</div>
          ) : result?.items.length ? (
            result.items.map((recipe) => (
              <button key={recipe.id} className="recipe-row" onClick={() => setEditing(recipe)}>
                <span className="recipe-icon"><Hammer /></span>
                <span className="recipe-summary">
                  <strong>{recipe.itemName}</strong>
                  <span>
                    {recipe.ingredients.length
                      ? recipe.ingredients.slice(0, 4).map((ingredient) => (
                          <em key={`${recipe.id}-${ingredient.resourceClass}`}>
                            {ingredient.resourceName} <b>×{ingredient.quantity}</b>
                          </em>
                        ))
                      : <i>No ingredients configured</i>}
                    {recipe.ingredients.length > 4 && <i>+{recipe.ingredients.length - 4} more</i>}
                  </span>
                </span>
                <span className={cx("mini-badge", recipe.overridden && "changed")}>
                  {recipe.overridden ? "Changed" : `${recipe.ingredients.length} ingredients`}
                </span>
                <ChevronRight size={19} />
              </button>
            ))
          ) : (
            <div className="empty-state"><Hammer /><strong>No matching recipes</strong><span>Try another item or resource name.</span></div>
          )}
        </div>
        <div className="pagination">
          <button disabled={(result?.page ?? 1) <= 1} onClick={() => setPage((value) => value - 1)}>
            <ChevronLeft size={17} /> Previous
          </button>
          <span>Page <strong>{result?.page ?? 1}</strong> of <strong>{result?.pages ?? 1}</strong></span>
          <button disabled={(result?.page ?? 1) >= (result?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>
            Next <ChevronRight size={17} />
          </button>
        </div>
      </section>

      {editing && result && (
        <CraftingRecipeModal
          recipe={editing}
          resources={result.resources}
          profile={profile}
          onClose={() => setEditing(undefined)}
          onSaved={async () => {
            setEditing(undefined);
            await Promise.all([loadRecipes(), reload()]);
            notify(`${editing.itemName} recipe saved.`);
          }}
          notify={notify}
        />
      )}
    </main>
  );
}

function CraftingRecipeModal({
  recipe,
  resources,
  profile,
  onClose,
  onSaved,
  notify
}: {
  recipe: CraftingRecipeDto;
  resources: Array<{ className: string; name: string }>;
  profile: ConfigProfile;
  onClose: () => void;
  onSaved: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [ingredients, setIngredients] = useState<CraftingIngredientDto[]>(() =>
    structuredClone(recipe.ingredients)
  );
  const [saving, setSaving] = useState(false);

  function updateIngredient(index: number, patch: Partial<CraftingIngredientDto>) {
    setIngredients((current) =>
      current.map((ingredient, itemIndex) =>
        itemIndex === index ? { ...ingredient, ...patch } : ingredient
      )
    );
  }

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal wide-modal recipe-modal">
        <div className="modal-heading">
          <div><small>CRAFTING RECIPE · {profile.name}</small><h2>{recipe.itemName}</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body">
          <div className="recipe-note">
            <ShieldCheck />
            <div>
              <strong>Use normal names—Wazy Control handles the config syntax.</strong>
              <span>Select resources and quantities below. Item paths never need to be typed.</span>
            </div>
          </div>
          <div className="ingredient-heading">
            <span>Resource</span><span>Quantity</span><span>Exact type</span><span />
          </div>
          <div className="ingredient-editor">
            {ingredients.map((ingredient, index) => (
              <div className="ingredient-row" key={`${ingredient.resourceClass}-${index}`}>
                <select
                  value={ingredient.resourceClass}
                  onChange={(event) => {
                    const selected = resources.find((resource) => resource.className === event.target.value);
                    if (selected) {
                      updateIngredient(index, {
                        resourceClass: selected.className,
                        resourceName: selected.name
                      });
                    }
                  }}
                >
                  {resources.map((resource) => (
                    <option value={resource.className} key={resource.className}>{resource.name}</option>
                  ))}
                </select>
                <input
                  aria-label={`${ingredient.resourceName} quantity`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={ingredient.quantity}
                  onChange={(event) => updateIngredient(index, { quantity: Number(event.target.value) })}
                />
                <label className="exact-toggle">
                  <input
                    type="checkbox"
                    checked={ingredient.exact}
                    onChange={(event) => updateIngredient(index, { exact: event.target.checked })}
                  />
                  <span>{ingredient.exact ? "Exact" : "Any substitute"}</span>
                </label>
                <button
                  className="icon-button danger"
                  title="Remove ingredient"
                  onClick={() => setIngredients((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                >
                  <Trash2 size={17} />
                </button>
              </div>
            ))}
          </div>
          <button
            className="button secondary add-ingredient"
            onClick={() => {
              const resource =
                resources.find((item) => !ingredients.some((ingredient) => ingredient.resourceClass === item.className)) ??
                resources[0];
              if (resource) {
                setIngredients((current) => [
                  ...current,
                  { resourceClass: resource.className, resourceName: resource.name, quantity: 1, exact: false }
                ]);
              }
            }}
          >
            <Plus size={17} /> Add ingredient
          </button>
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button
            className="button primary"
            disabled={saving || !ingredients.length || ingredients.some((ingredient) => ingredient.quantity < 0)}
            onClick={async () => {
              setSaving(true);
              try {
                await api.saveCraftingRecipe(profile.id, recipe.id, ingredients);
                await onSaved();
              } catch (error) {
                notify(error instanceof Error ? error.message : String(error), "error");
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? <LoaderCircle className="spin" /> : <Save size={17} />} Save recipe
          </button>
        </div>
      </div>
    </div>
  );
}

type GeneralDraft = Record<string, { configured: boolean; value: string | number | boolean }>;

function GeneralSettingsEditor({
  profile,
  notify,
  reload
}: {
  profile: ConfigProfile;
  notify: (message: string, tone?: "success" | "error") => void;
  reload: () => Promise<void>;
}) {
  const [settings, setSettings] = useState<GeneralSettingDto[]>([]);
  const [draft, setDraft] = useState<GeneralDraft>({});
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.generalSettings(profile.id);
      setSettings(result.items);
      setDraft(Object.fromEntries(result.items.map((item) => [
        item.id,
        { configured: item.configured, value: item.value }
      ])));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, notify]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const dirtyIds = useMemo(
    () => settings
      .filter((setting) => {
        const current = draft[setting.id];
        return current && (
          current.configured !== setting.configured ||
          String(current.value) !== String(setting.value)
        );
      })
      .map((setting) => setting.id),
    [draft, settings]
  );

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return settings.filter((setting) => {
      const current = draft[setting.id];
      if (activeOnly && !current?.configured) return false;
      if (!normalized) return true;
      return [
        setting.label,
        setting.description,
        setting.group,
        setting.key,
        setting.file
      ].some((value) => value.toLowerCase().includes(normalized));
    });
  }, [activeOnly, draft, query, settings]);

  const groups = useMemo(() => {
    const grouped = new Map<string, GeneralSettingDto[]>();
    for (const setting of visible) {
      const members = grouped.get(setting.group) ?? [];
      members.push(setting);
      grouped.set(setting.group, members);
    }
    return [...grouped.entries()];
  }, [visible]);

  function updateSetting(id: string, patch: Partial<GeneralDraft[string]>) {
    setDraft((current) => ({
      ...current,
      [id]: { ...current[id], ...patch }
    }));
  }

  function dependencySatisfied(setting: GeneralSettingDto): boolean {
    const requirement = setting.constraints.other;
    if (!requirement) return true;
    const parent = draft[requirement.key];
    return Boolean(parent?.configured && Boolean(parent.value) === requirement.value);
  }

  async function saveChanges() {
    const changes: GeneralSettingChangeDto[] = dirtyIds.map((id) => ({
      id,
      configured: draft[id].configured,
      value: draft[id].value
    }));
    if (!changes.length) return;
    setSaving(true);
    try {
      const result = await api.saveGeneralSettings(profile.id, changes);
      setSettings(result.items);
      setDraft(Object.fromEntries(result.items.map((item) => [
        item.id,
        { configured: item.configured, value: item.value }
      ])));
      await reload();
      notify(`${changes.length} General Settings changes saved.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="page config-page general-settings-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><SlidersHorizontal size={24} /></span>
          <div>
            <small>COMPLETE ARK SETTINGS</small>
            <h1>General Settings</h1>
            <p>Every common server rule in one editor. Technical INI keys are generated automatically.</p>
          </div>
        </div>
        <div className="page-actions">
          <a className="button secondary" href={`/api/config/${profile.id}/GameUserSettings.ini/download`}>
            <Download size={17} /> Export
          </a>
          <button
            className="button primary"
            disabled={!dirtyIds.length || saving}
            onClick={() => void saveChanges()}
          >
            {saving ? <LoaderCircle className="spin" /> : <Save size={17} />}
            Save {dirtyIds.length ? `${dirtyIds.length} changes` : "changes"}
          </button>
        </div>
      </section>

      <section className="panel general-settings-browser">
        <div className="config-toolbar">
          <div className="general-toolbar-left">
            <label className="switch-label general-active-filter">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
              />
              <i />
              <span>Show configured only</span>
            </label>
            <span className="general-count">
              <strong>{visible.length}</strong> of {settings.length} options
            </span>
          </div>
          <label className="search-box">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search cryopods, turrets, tribes, XP..."
            />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>

        <div className="general-profile-strip">
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
          <span>
            <i className="general-dot base" /> Base INI
            <i className="general-dot changed" /> Profile change
            <i className="general-dot unset" /> Not set
          </span>
        </div>

        {loading ? (
          <div className="table-loading"><LoaderCircle className="spin" /> Loading General Settings...</div>
        ) : groups.length ? (
          <div className="general-groups">
            {groups.map(([group, members]) => (
              <section className="general-group" key={group}>
                <header>
                  <div><strong>{group}</strong><span>{members.length} options</span></div>
                </header>
                <div>
                  {members.map((setting) => {
                    const current = draft[setting.id] ?? {
                      configured: setting.configured,
                      value: setting.value
                    };
                    const enabledByDependency = dependencySatisfied(setting);
                    const dirty = dirtyIds.includes(setting.id);
                    const source = !current.configured
                      ? "not-set"
                      : dirty || setting.source === "profile"
                        ? "profile"
                        : "base";
                    const oneOf = setting.constraints.oneof ?? [];
                    const listId = `general-options-${setting.id}`;
                    return (
                      <article
                        className={cx(
                          "general-setting-row",
                          !current.configured && "not-configured",
                          !enabledByDependency && "dependency-disabled",
                          dirty && "dirty"
                        )}
                        key={setting.id}
                      >
                        <div className="general-setting-info">
                          <strong>{setting.label}</strong>
                          <p>{setting.description}</p>
                          <span>
                            {setting.file}
                            {setting.launchOption && " · launch option"}
                            {setting.contentPackName !== "Ark Prime" && ` · ${setting.contentPackName}`}
                          </span>
                        </div>

                        <div className="general-setting-control">
                          {setting.valueType === "boolean" ? (
                            <label className="general-value-switch">
                              <input
                                type="checkbox"
                                checked={Boolean(current.value)}
                                disabled={!current.configured || !enabledByDependency}
                                onChange={(event) => updateSetting(setting.id, { value: event.target.checked })}
                              />
                              <i />
                              <span>{current.value ? "On" : "Off"}</span>
                            </label>
                          ) : setting.valueType === "numeric" ? (
                            <input
                              type="number"
                              value={String(current.value)}
                              min={setting.constraints.min}
                              max={setting.constraints.max}
                              step="any"
                              disabled={!current.configured || !enabledByDependency}
                              placeholder={String(setting.defaultValue)}
                              onChange={(event) => updateSetting(setting.id, {
                                value: event.target.value === "" ? "" : Number(event.target.value)
                              })}
                            />
                          ) : oneOf.length && setting.constraints.allowcustom === false ? (
                            <select
                              value={String(current.value)}
                              disabled={!current.configured || !enabledByDependency}
                              onChange={(event) => updateSetting(setting.id, { value: event.target.value })}
                            >
                              {!oneOf.includes(String(current.value)) && <option value={String(current.value)}>{String(current.value) || "Choose..."}</option>}
                              {oneOf.map((option) => <option value={option} key={option}>{option || "None"}</option>)}
                            </select>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={String(current.value)}
                                list={oneOf.length ? listId : undefined}
                                disabled={!current.configured || !enabledByDependency}
                                placeholder={String(setting.defaultValue)}
                                onChange={(event) => updateSetting(setting.id, { value: event.target.value })}
                              />
                              {oneOf.length > 0 && (
                                <datalist id={listId}>
                                  {oneOf.map((option) => <option value={option} key={option} />)}
                                </datalist>
                              )}
                            </>
                          )}
                        </div>

                        <span className={cx("general-source", source)}>
                          {source === "profile" ? "Changed" : source === "base" ? "Base" : "Not set"}
                        </span>
                        <button
                          className={cx("icon-button", current.configured ? "remove-general" : "add-general")}
                          title={current.configured ? "Do not write this setting" : "Use this setting"}
                          disabled={!enabledByDependency && !current.configured}
                          onClick={() => updateSetting(setting.id, { configured: !current.configured })}
                        >
                          {current.configured ? <X size={16} /> : <Plus size={16} />}
                        </button>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <SlidersHorizontal />
            <strong>No matching options</strong>
            <span>Try another search or turn off the configured-only filter.</span>
          </div>
        )}
      </section>

      {dirtyIds.length > 0 && (
        <div className="general-save-dock">
          <span><strong>{dirtyIds.length}</strong> unsaved changes</span>
          <button
            className="button secondary"
            onClick={() => setDraft(Object.fromEntries(settings.map((item) => [
              item.id,
              { configured: item.configured, value: item.value }
            ])))}
          >
            Reset
          </button>
          <button className="button primary" disabled={saving} onClick={() => void saveChanges()}>
            {saving ? <LoaderCircle className="spin" /> : <Save size={17} />} Save changes
          </button>
        </div>
      )}
    </main>
  );
}

function ConfigEditor({
  data,
  profile,
  category,
  notify,
  reload
}: {
  data: Bootstrap;
  profile: ConfigProfile;
  category: string;
  notify: (message: string, tone?: "success" | "error") => void;
  reload: () => Promise<void>;
}) {
  const definition = data.editors.find((item) => item.id === category) ?? data.editors.at(-1)!;
  const Icon = iconMap[definition.icon] ?? Braces;
  const [file, setFile] = useState<ConfigFileName>("Game.ini");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<{ items: IniEntryDto[]; total: number; page: number; pages: number }>();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<IniEntryDto | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.entries(profile.id, file, { category, q: debouncedQuery, page }));
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setLoading(false);
    }
  }, [profile.id, file, category, debouncedQuery, page, notify]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => setPage(1), [file, category, debouncedQuery, profile.id]);

  return (
    <main className="page config-page">
      <section className="page-title-row">
        <div className="title-with-icon">
          <span><Icon size={24} /></span>
          <div><small>CONFIG EDITOR</small><h1>{definition.label}</h1><p>{definition.description}</p></div>
        </div>
        <div className="page-actions">
          <a className="button secondary" href={`/api/config/${profile.id}/${file}/download`}>
            <Download size={17} /> Export
          </a>
          <button className="button primary" onClick={() => setAdding(true)}>
            <Plus size={17} /> New override
          </button>
        </div>
      </section>

      <section className="panel config-panel">
        <div className="config-toolbar">
          <div className="segmented">
            {(["Game.ini", "GameUserSettings.ini", "Engine.ini"] as ConfigFileName[]).map((name) => (
              <button className={name === file ? "active" : ""} onClick={() => setFile(name)} key={name}>{name}</button>
            ))}
          </div>
          <label className="search-box">
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${definition.label.toLowerCase()}…`} />
            {query && <button onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
        </div>

        <div className="table-meta">
          <span><strong>{result?.total.toLocaleString() ?? "—"}</strong> matching entries</span>
          <span>Editing profile <b style={{ color: profile.color }}>{profile.name}</b></span>
        </div>

        <div className="config-table-wrap">
          <table className="config-table">
            <thead><tr><th>Setting</th><th>Current value</th><th>Profile</th><th /></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}><div className="table-loading"><LoaderCircle className="spin" /> Indexing config…</div></td></tr>
              ) : result?.items.length ? result.items.map((entry) => (
                <tr className={cx(entry.deleted && "deleted-row")} key={entry.id}>
                  <td>
                    <strong>{entry.displayName ?? entry.key}</strong>
                    <small>{definition.label}</small>
                  </td>
                  <td>
                    <span className="friendly-summary">{entry.deleted ? "Removed in this profile" : (entry.valueSummary ?? entry.valuePreview) || "(empty)"}</span>
                  </td>
                  <td>
                    {entry.overridden ? <span className="mini-badge changed">Changed</span> : <span className="mini-badge">Base</span>}
                  </td>
                  <td><button className="icon-button" title="Edit" onClick={() => setEditing(entry)}><Pencil size={16} /></button></td>
                </tr>
              )) : (
                <tr><td colSpan={4}><div className="empty-state"><Box /><strong>No matching config entries</strong><span>Try another file or create a new override.</span></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <button disabled={(result?.page ?? 1) <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /> Previous</button>
          <span>Page <strong>{result?.page ?? 1}</strong> of <strong>{result?.pages ?? 1}</strong></span>
          <button disabled={(result?.page ?? 1) >= (result?.pages ?? 1)} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight size={17} /></button>
        </div>
      </section>

      {(editing || adding) && (
        <EntryModal
          profile={profile}
          file={file}
          entry={editing}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={async () => {
            setEditing(null); setAdding(false);
            await Promise.all([loadEntries(), reload()]);
            notify("Profile override saved.");
          }}
          notify={notify}
        />
      )}
    </main>
  );
}

function EntryModal({
  profile,
  file,
  entry,
  onClose,
  onSaved,
  notify
}: {
  profile: ConfigProfile;
  file: ConfigFileName;
  entry: IniEntryDto | null;
  onClose: () => void;
  onSaved: () => void;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [detail, setDetail] = useState<IniEntryDto | null>(entry);
  const [section, setSection] = useState(entry?.section ?? (file === "Game.ini" ? "/script/shootergame.shootergamemode" : file === "GameUserSettings.ini" ? "ServerSettings" : "Core.System"));
  const [key, setKey] = useState(entry?.key ?? "");
  const [value, setValue] = useState(entry?.value ?? "");
  const [deleted, setDeleted] = useState(entry?.deleted ?? false);
  const [friendlyFields, setFriendlyFields] = useState(() =>
    structuredClone(entry?.friendlyForm?.fields ?? [])
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry || (entry.value || entry.valueLength <= 8_000)) return;
    void api.entry(profile.id, file, entry.id).then((full) => {
      setDetail(full);
      setValue(full.value);
      setFriendlyFields(structuredClone(full.friendlyForm?.fields ?? []));
    }).catch((error) => notify(error.message, "error"));
  }, [entry, profile.id, file, notify]);

  async function save() {
    setSaving(true);
    try {
      await api.saveEntry(profile.id, {
        id: detail?.overridden ? detail.id : undefined,
        file,
        section,
        key,
        occurrence: entry?.occurrence ?? 0,
        value,
        deleted
      });
      onSaved();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal entry-modal" role="dialog" aria-modal="true">
        <div className="modal-heading">
          <div><small>{entry ? "EDIT SETTING" : "NEW CONFIG OVERRIDE"}</small><h2>{detail?.displayName ?? entry?.displayName ?? "Add setting"}</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </div>
        <div className="modal-body form-grid">
          <label><span>Profile</span><input value={profile.name} disabled /></label>
          <label><span>Config file</span><input value={file} disabled /></label>
          {detail?.friendlyForm && friendlyFields.length > 0 && (
            <div className="friendly-form full">
              {friendlyFields.map((field, index) =>
                field.type === "boolean" ? (
                  <label className="friendly-toggle" key={field.id}>
                    <input
                      type="checkbox"
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        const next = friendlyFields.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, value: event.target.checked } : item
                        );
                        setFriendlyFields(next);
                        setValue(applyFriendlyValue(value, detail.friendlyForm!.kind, next));
                      }}
                    />
                    <i />
                    <span><strong>{field.label}</strong><small>{field.value ? "Enabled" : "Disabled"}</small></span>
                  </label>
                ) : (
                  <label key={field.id}>
                    <span>{field.label}</span>
                    <div className="friendly-input">
                      <input
                        type={field.type}
                        step={field.type === "number" ? "0.01" : undefined}
                        value={String(field.value)}
                        onChange={(event) => {
                          const nextValue = field.type === "number" ? Number(event.target.value) : event.target.value;
                          const next = friendlyFields.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, value: nextValue } : item
                          );
                          setFriendlyFields(next);
                          setValue(applyFriendlyValue(value, detail.friendlyForm!.kind, next));
                        }}
                      />
                      {field.suffix && <b>{field.suffix}</b>}
                    </div>
                  </label>
                )
              )}
            </div>
          )}
          {entry && !detail?.friendlyForm && (
            <div className="recipe-note full">
              <SlidersHorizontal />
              <div>
                <strong>{detail?.valueSummary ?? entry.valueSummary ?? "Advanced ARK rule"}</strong>
                <span>The setting has a readable name. Its exact syntax is available under Advanced.</span>
              </div>
            </div>
          )}
          <details className="advanced-config full" open={!entry}>
            <summary>Advanced · raw ARK config</summary>
            <div className="form-grid">
              <label className="full"><span>Section</span><input value={section} onChange={(event) => setSection(event.target.value)} /></label>
              <label className="full"><span>Internal key</span><input value={key} onChange={(event) => setKey(event.target.value)} disabled={Boolean(entry)} /></label>
              <label className="full"><span>Generated value</span><textarea rows={Math.min(16, Math.max(5, value.split("\n").length + 2))} value={value} onChange={(event) => setValue(event.target.value)} /></label>
            </div>
          </details>
          {entry && (
            <label className="check-row full">
              <input type="checkbox" checked={deleted} onChange={(event) => setDeleted(event.target.checked)} />
              <span><strong>Remove this entry from {profile.name}</strong><small>The base file remains untouched.</small></span>
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={saving || !section || !key} onClick={save}>
            {saving ? <LoaderCircle className="spin" size={17} /> : <Save size={17} />} Save override
          </button>
        </div>
      </div>
    </div>
  );
}

function ServersPage({
  data,
  notify,
  reload
}: {
  data: Bootstrap;
  notify: (message: string, tone?: "success" | "error") => void;
  reload: () => Promise<void>;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<ArkServer>();
  const [powerAction, setPowerAction] = useState<ClusterPowerAction>();
  const [powerResults, setPowerResults] = useState<ClusterPowerResult[]>();
  const [powering, setPowering] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      await api.refreshNitrado();
      await reload();
      notify("Nitrado server states refreshed.");
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setRefreshing(false);
    }
  }
  return (
    <main className="page">
      <section className="page-title-row">
        <div><small>NITRADO CLUSTER</small><h1>Servers</h1><p>All 12 services found in your Wazy project.</p></div>
        <div className="page-actions server-power-actions">
          <button className="button start-button" disabled={refreshing || powering || !data.integrations.nitrado.configured} onClick={() => {
            setPowerResults(undefined);
            setPowerAction("start");
          }}><Power size={17} /> Start all</button>
          <button className="button danger-outline" disabled={refreshing || powering || !data.integrations.nitrado.configured} onClick={() => {
            setPowerResults(undefined);
            setPowerAction("stop");
          }}><PowerOff size={17} /> Stop all</button>
          <button className="button secondary" disabled={refreshing || powering} onClick={refresh}>
            <RotateCw className={refreshing ? "spin" : ""} size={17} /> Refresh status
          </button>
        </div>
      </section>
      {!data.integrations.nitrado.configured && (
        <div className="notice warning wide"><AlertTriangle /><div><strong>Nitrado token is not configured</strong><span>Open Settings and securely add your Nitrado long-life token.</span></div></div>
      )}
      <section className="server-grid">
        {data.state.servers.map((server) => (
          <article className={cx("server-card", !server.enabled && "disabled")} key={server.id}>
            <div className="server-card-top">
              <span className="map-monogram">{server.map.split(" ").map((word) => word[0]).join("").slice(0, 2)}</span>
              <StatusPill status={server.status} />
            </div>
            <h2>{server.map}</h2>
            <p>{server.name}</p>
            <dl>
              <div><dt>Address</dt><dd>{server.address}:{server.port}</dd></div>
              <div><dt>Service ID</dt><dd>{server.serviceId}</dd></div>
              <div><dt>Config sets</dt><dd>{server.configSets.length}</dd></div>
            </dl>
            <div className="server-card-footer">
              <label className="switch-label">
                <input type="checkbox" checked={server.enabled} onChange={async (event) => {
                  await api.patchServer(server.id, { enabled: event.target.checked });
                  await reload();
                }} />
                <i /> Enabled
              </label>
              <button className="text-button" onClick={() => setSelected(server)}>Configure <ChevronRight size={15} /></button>
            </div>
          </article>
        ))}
      </section>
      {selected && (
        <ServerModal
          server={selected}
          configSets={data.beacon?.configSets.map((set) => set.name) ?? []}
          onClose={() => setSelected(undefined)}
          onSaved={async () => {
            setSelected(undefined);
            await reload();
            notify("Server settings and config sets saved.");
          }}
          notify={notify}
        />
      )}
      {powerAction && (
        <ClusterPowerModal
          action={powerAction}
          servers={data.state.servers.filter((server) => server.enabled)}
          results={powerResults}
          running={powering}
          onClose={() => {
            if (powering) return;
            setPowerAction(undefined);
            setPowerResults(undefined);
          }}
          onConfirm={async () => {
            setPowering(true);
            try {
              const result = await api.clusterPower(powerAction);
              setPowerResults(result.results);
              await reload();
              notify(
                `${powerAction === "start" ? "Start" : "Stop"} command accepted for ${result.accepted} server${result.accepted === 1 ? "" : "s"}.`,
                result.failed ? "error" : "success"
              );
            } catch (error) {
              notify(error instanceof Error ? error.message : String(error), "error");
            } finally {
              setPowering(false);
            }
          }}
        />
      )}
    </main>
  );
}

function ClusterPowerModal({
  action,
  servers,
  results,
  running,
  onClose,
  onConfirm
}: {
  action: ClusterPowerAction;
  servers: ArkServer[];
  results?: ClusterPowerResult[];
  running: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const starting = action === "start";
  const accepted = results?.filter((result) => result.status === "accepted").length ?? 0;
  const skipped = results?.filter((result) => result.status === "skipped").length ?? 0;
  const failed = results?.filter((result) => result.status === "failed").length ?? 0;
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal wide-modal cluster-power-modal">
        <div className="modal-heading">
          <div><small>NITRADO CLUSTER POWER</small><h2>{results ? `${starting ? "Start" : "Stop"} results` : `${starting ? "Start" : "Stop"} all servers?`}</h2></div>
          <button className="icon-button" disabled={running} onClick={onClose}><X /></button>
        </div>
        <div className="modal-body">
          {!results ? (
            <>
              <div className={cx("notice wide", starting ? "success" : "danger")}>
                {starting ? <Power /> : <AlertTriangle />}
                <div>
                  <strong>{starting ? `Start ${servers.length} enabled servers` : `Stop ${servers.length} enabled servers`}</strong>
                  <span>The command is sent to every enabled server at the same time. Servers already in the requested state are safely skipped.</span>
                </div>
              </div>
              <div className="cluster-target-list">
                {servers.map((server) => <span key={server.id}><b>{server.map}</b><small>{server.status ?? "unknown"}</small></span>)}
              </div>
              <p className="cluster-power-help">This changes only server power state. No config files, PINs, passwords or world saves are changed.</p>
            </>
          ) : (
            <>
              <div className="power-result-summary">
                <span><b>{accepted}</b><small>Accepted</small></span>
                <span><b>{skipped}</b><small>Already done</small></span>
                <span className={failed ? "failed" : ""}><b>{failed}</b><small>Failed</small></span>
              </div>
              <div className="power-result-list">
                {results.map((result) => (
                  <div key={result.serverId}>
                    <span><strong>{result.map}</strong><small>{result.message}</small></span>
                    <StatusPill status={result.status} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          {results ? (
            <button className="button primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="button secondary" disabled={running} onClick={onClose}>Cancel</button>
              <button className={cx("button", starting ? "primary" : "danger-button")} disabled={running || !servers.length} onClick={onConfirm}>
                {running ? <LoaderCircle className="spin" size={17} /> : starting ? <Power size={17} /> : <PowerOff size={17} />}
                {running ? "Sending commands..." : starting ? "Start all servers" : "Stop all servers"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ServerModal({
  server,
  configSets,
  onClose,
  onSaved,
  notify
}: {
  server: ArkServer;
  configSets: string[];
  onClose: () => void;
  onSaved: () => void;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [name, setName] = useState(server.name);
  const [root, setRoot] = useState(server.remoteRoot);
  const [activeSets, setActiveSets] = useState(() => new Set(server.configSets));
  const [saving, setSaving] = useState(false);
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal wide-modal server-modal">
        <div className="modal-heading"><div><small>SERVER CONFIGURATION</small><h2>{server.map}</h2></div><button className="icon-button" onClick={onClose}><X /></button></div>
        <div className="modal-body form-grid">
          <label className="full"><span>Server name</span><input value={name} onChange={(event) => setName(event.target.value)} /></label>
          <label><span>Nitrado service ID</span><input value={server.serviceId} disabled /></label>
          <label><span>Address</span><input value={`${server.address}:${server.port}`} disabled /></label>
          <label className="full"><span>Remote config root</span><input value={root} onChange={(event) => setRoot(event.target.value)} placeholder="Auto-discover from Nitrado (recommended)" /><small>Leave empty to detect /games/.../ShooterGame/Saved/Config/WindowsServer.</small></label>
          <div className="full field-block">
            <span>Active config sets ({activeSets.size} of {configSets.length})</span>
            <p className="field-help">Enable exactly which Base, map and timed sets this server should receive.</p>
            <div className="config-set-selector">
              {configSets.map((set) => (
                <label className={activeSets.has(set) ? "selected" : ""} key={set}>
                  <input
                    type="checkbox"
                    checked={activeSets.has(set)}
                    onChange={(event) =>
                      setActiveSets((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(set);
                        else next.delete(set);
                        return next;
                      })
                    }
                  />
                  <span><Check size={14} />{set}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer"><button className="button secondary" onClick={onClose}>Cancel</button><button className="button primary" disabled={saving} onClick={async () => {
          setSaving(true);
          try { await api.patchServer(server.id, { name, remoteRoot: root, configSets: [...activeSets] }); onSaved(); }
          catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); }
          finally { setSaving(false); }
        }}><Save size={17} /> Save</button></div>
      </div>
    </div>
  );
}

function AutomationPage({ data, notify, reload }: { data: Bootstrap; notify: (message: string, tone?: "success" | "error") => void; reload: () => Promise<void> }) {
  const [wipeAt, setWipeAt] = useState(() => {
    const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  });
  const [selectedServers, setSelectedServers] = useState(() => new Set(data.state.servers.filter((item) => item.enabled).map((item) => item.id)));
  const [selectedStages, setSelectedStages] = useState(
    () => new Set(data.state.profiles.filter((profile) => ["24h", "72h", "5d"].includes(profile.id)).map((profile) => profile.id))
  );
  const [creating, setCreating] = useState(false);
  return (
    <main className="page">
      <section className="page-title-row">
        <div><small>24H / 72H / 5 DAYS</small><h1>Automation</h1><p>The wipe timestamp is a reference point; only selected stages deploy automatically.</p></div>
      </section>
      <section className="automation-layout">
        <div className="panel schedule-builder">
          <div className="panel-heading"><div><small>NEW SCHEDULE</small><h2>Wipe release plan</h2></div><CalendarClock /></div>
          <label className="large-field"><span>Wipe starts at</span><input type="datetime-local" value={wipeAt} onChange={(event) => setWipeAt(event.target.value)} /></label>
          <div className={cx("automation-zero-state", selectedStages.has("launch") && "launch-enabled")}>
            {selectedStages.has("launch") ? <AlertTriangle /> : <CheckCircle2 />}
            <div>
              <strong>{selectedStages.has("launch") ? "Wipe-start deployment enabled" : "No deployment at wipe start"}</strong>
              <span>{selectedStages.has("launch") ? "Wipe launch will change selected servers at the entered time." : "You start the servers manually. Automation begins with the first selected timed stage."}</span>
            </div>
          </div>
          <div className="field-block">
            <span>Stages ({selectedStages.size} selected)</span>
            <div className="stage-list">
              {data.state.profiles.map((profile) => (
                <label className={cx("stage-row", selectedStages.has(profile.id) && "selected")} key={profile.id}>
                  <input type="checkbox" checked={selectedStages.has(profile.id)} onChange={(event) => {
                    setSelectedStages((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(profile.id); else next.delete(profile.id);
                      return next;
                    });
                  }} />
                  <i style={{ background: profile.color }} />
                  <div><strong>{profile.name}</strong><small>{profile.description}</small></div>
                  <b>{profile.offsetHours ? `+${profile.offsetHours}h` : selectedStages.has(profile.id) ? "WIPE" : "MANUAL"}</b>
                </label>
              ))}
            </div>
          </div>
          <div className="field-block">
            <span>Servers ({selectedServers.size}/{data.state.servers.length})</span>
            <div className="server-check-grid">
              {data.state.servers.map((server) => (
                <label key={server.id}><input type="checkbox" checked={selectedServers.has(server.id)} onChange={(event) => {
                  setSelectedServers((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(server.id); else next.delete(server.id);
                    return next;
                  });
                }} /><span>{server.map}</span></label>
              ))}
            </div>
          </div>
          <button className="button primary full-button" disabled={creating || !selectedServers.size || !selectedStages.size} onClick={async () => {
            setCreating(true);
            try {
              await api.createSchedule({
                name: `Wazy wipe · ${new Date(wipeAt).toLocaleDateString()}`,
                wipeAt: new Date(wipeAt).toISOString(),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                serverIds: [...selectedServers],
                enabled: true,
                stages: data.state.profiles.map((profile) => ({
                  profileId: profile.id,
                  offsetHours: profile.offsetHours,
                  enabled: selectedStages.has(profile.id)
                }))
              });
              await reload();
              notify("Wipe schedule created.");
            } catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); }
            finally { setCreating(false); }
          }}>{creating ? <LoaderCircle className="spin" /> : <CalendarClock />} Create automation</button>
        </div>
        <div className="stack">
          <div className="panel">
            <div className="panel-heading"><div><small>ACTIVE PLANS</small><h2>Schedules</h2></div><span className="count-badge">{data.state.schedules.length}</span></div>
            <div className="schedule-list">
              {data.state.schedules.length ? data.state.schedules.map((schedule) => (
                <article key={schedule.id}>
                  <div className="schedule-icon"><CalendarClock /></div>
                  <div><strong>{schedule.name}</strong><span>{new Date(schedule.wipeAt).toLocaleString()} · {schedule.serverIds.length} servers</span><small>{schedule.stages.filter((stage) => stage.enabled && stage.executedAt).length}/{schedule.stages.filter((stage) => stage.enabled).length} enabled stages executed</small></div>
                  <label className="switch-label"><input type="checkbox" checked={schedule.enabled} onChange={async (event) => { await api.patchSchedule(schedule.id, { enabled: event.target.checked }); await reload(); }} /><i /></label>
                  <button className="icon-button danger" onClick={async () => { await api.deleteSchedule(schedule.id); await reload(); notify("Schedule removed."); }}><Trash2 size={16} /></button>
                </article>
              )) : <div className="empty-state"><CalendarClock /><strong>No schedules yet</strong><span>Create your first automated wipe plan.</span></div>}
            </div>
          </div>
          <div className="notice info wide"><ShieldCheck /><div><strong>Idempotent execution</strong><span>Each stage is recorded before it runs, so a droplet restart cannot apply the same transition twice.</span></div></div>
        </div>
      </section>
    </main>
  );
}

function DeploymentsPage({ data }: { data: Bootstrap }) {
  const [open, setOpen] = useState<DeploymentRecord>();
  return (
    <main className="page">
      <section className="page-title-row"><div><small>AUDIT LOG</small><h1>Deployments</h1><p>Every server step, hash verification and error in one place.</p></div></section>
      <section className="panel">
        <div className="deployment-list">
          {data.state.deployments.length ? data.state.deployments.map((deployment) => (
            <button key={deployment.id} onClick={() => setOpen(deployment)}>
              <span className={cx("deployment-state", deployment.status)}>{deployment.status === "running" ? <LoaderCircle className="spin" /> : deployment.status === "succeeded" ? <CheckCircle2 /> : <CircleDot />}</span>
              <span><strong>{profileName(data, deployment.profileId)}</strong><small>{deployment.dryRun ? "Dry run" : "Live deploy"} · {deployment.serverIds.length} servers · {deployment.triggeredBy}</small></span>
              <time>{new Date(deployment.requestedAt).toLocaleString()}</time>
              <StatusPill status={deployment.status} />
              <ChevronRight />
            </button>
          )) : <div className="empty-state tall"><History /><strong>No deployment history</strong><span>Use Deploy and start with a dry run.</span></div>}
        </div>
      </section>
      {open && <DeploymentModal deployment={open} data={data} onClose={() => setOpen(undefined)} />}
    </main>
  );
}

function DeploymentModal({ deployment, data, onClose }: { deployment: DeploymentRecord; data: Bootstrap; onClose: () => void }) {
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal wide-modal">
        <div className="modal-heading"><div><small>{deployment.id}</small><h2>{profileName(data, deployment.profileId)}</h2></div><button className="icon-button" onClick={onClose}><X /></button></div>
        <div className="modal-body">
          <div className="deployment-results">
            {deployment.results.map((result) => {
              const server = data.state.servers.find((item) => item.id === result.serverId);
              return <article key={result.serverId}><div className="result-heading"><strong>{server?.map ?? result.serverId}</strong><StatusPill status={result.status} /></div><div className="event-log">{result.events.map((event, index) => <div className={event.level} key={`${event.at}-${index}`}><time>{new Date(event.at).toLocaleTimeString()}</time><span>{event.message}</span></div>)}</div></article>;
            })}
          </div>
        </div>
        <div className="modal-footer"><button className="button secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function ConfigSetsPage({ data, notify, reload }: { data: Bootstrap; notify: (message: string, tone?: "success" | "error") => void; reload: () => Promise<void> }) {
  const [selected, setSelected] = useState<BeaconSetSummary>();
  const sets = data.beacon?.configSets ?? [];
  return (
    <main className="page">
      <section className="page-title-row">
        <div><small>BEACON CONFIG SETS</small><h1>Config Sets</h1><p>Edit custom set contents and choose exactly which wipe stages use each set.</p></div>
      </section>
      <section className="config-set-manager-grid">
        {sets.map((set) => {
          const activeProfiles = data.state.profiles.filter((profile) => profile.activeConfigSets.includes(set.name));
          const creatureCount = set.creatureAdjustments?.length ?? 0;
          const hotZoneCount = set.hotZone?.creatures.length ?? 0;
          return (
            <button className="config-set-manager-card" key={set.id} onClick={() => setSelected(set)}>
              <span className="config-set-manager-icon"><Layers3 /></span>
              <span className="config-set-manager-copy">
                <strong>{set.name}</strong>
                <small>{set.hotZone ? `${hotZoneCount} Hot Zone creatures` : `${creatureCount} creature adjustment${creatureCount === 1 ? "" : "s"}`} · {set.editors.reduce((sum, editor) => sum + editor.count, 0).toLocaleString()} total objects</small>
                <span className="chip-row">
                  {activeProfiles.map((profile) => <em key={profile.id}>{profile.name}</em>)}
                  {!activeProfiles.length && <em className="muted">Not active in any profile</em>}
                </span>
              </span>
              <ChevronRight />
            </button>
          );
        })}
      </section>
      {selected && <ConfigSetModal configSet={selected} allConfigSets={sets} profiles={data.state.profiles} onClose={() => setSelected(undefined)} onSaved={async () => { setSelected(undefined); await reload(); notify("Config set saved and deployment rules updated."); }} notify={notify} />}
    </main>
  );
}

function ConfigSetModal({ configSet, allConfigSets, profiles, onClose, onSaved, notify }: {
  configSet: BeaconSetSummary;
  allConfigSets: BeaconSetSummary[];
  profiles: ConfigProfile[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  notify: (message: string, tone?: "success" | "error") => void;
}) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.configSet>>>();
  const [adjustments, setAdjustments] = useState<ConfigSetCreatureAdjustmentDto[]>([]);
  const [hotZone, setHotZone] = useState<HotZoneConfigDto>();
  const [activeProfiles, setActiveProfiles] = useState<Set<string>>(new Set());
  const [newCreature, setNewCreature] = useState("");
  const [saving, setSaving] = useState(false);
  const metrics = [
    ["wildDamage", "Wild damage"],
    ["wildResistance", "Wild resistance"],
    ["tamedDamage", "Tamed damage"],
    ["tamedResistance", "Tamed resistance"]
  ] as const;

  useEffect(() => {
    void api.configSet(configSet.id).then((loaded) => {
      setDetail(loaded);
      setAdjustments(loaded.set.creatureAdjustments ?? []);
      setHotZone(loaded.set.hotZone ? structuredClone(loaded.set.hotZone) : undefined);
      setActiveProfiles(new Set(loaded.activeProfileIds));
      const used = new Set(
        (loaded.set.hotZone?.creatures ?? loaded.set.creatureAdjustments ?? []).map((creature) => creature.className)
      );
      setNewCreature(loaded.creatures.find((creature) => !used.has(creature.className))?.className ?? "");
    }).catch((error) => notify(error instanceof Error ? error.message : String(error), "error"));
  }, [configSet.id, notify]);

  function updateAdjustment(index: number, patch: Partial<ConfigSetCreatureAdjustmentDto>) {
    setAdjustments((current) => current.map((item, target) => target === index ? { ...item, ...patch } : item));
  }

  function addCreature() {
    const creature = detail?.creatures.find((item) => item.className === newCreature);
    if (!creature) return;
    if (hotZone) {
      if (hotZone.creatures.some((item) => item.className === creature.className)) return;
      setHotZone({
        ...hotZone,
        creatures: [...hotZone.creatures, {
          className: creature.className,
          name: creature.name,
          weight: 1,
          maxPercentage: 0.01
        }]
      });
      return;
    }
    if (adjustments.some((item) => item.className === creature.className)) return;
    setAdjustments((current) => [...current, {
      className: creature.className,
      name: creature.name,
      contentPackName: creature.contentPackName,
      configured: ["wildDamage"],
      wildDamage: 1,
      wildResistance: 1,
      tamedDamage: 1,
      tamedResistance: 1,
      prohibitSpawning: false,
      preventTaming: false,
      prohibitTransfer: false,
      prohibitBreeding: false
    }]);
  }

  function hotZoneBlockers(className: string): string[] {
    return allConfigSets
      .filter((set) => set.id !== configSet.id)
      .filter((set) =>
        (set.creatureAdjustments ?? []).some((creature) => creature.className === className && creature.prohibitSpawning) ||
        (set.stage?.creatures ?? []).some((creature) => creature.classString === className && creature.prohibitSpawning)
      )
      .map((set) => set.name);
  }

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal config-set-editor-modal">
        <div className="modal-heading"><div><small>EDIT CONFIG SET</small><h2>{configSet.name}</h2></div><button className="icon-button" onClick={onClose}><X /></button></div>
        <div className="modal-body config-set-editor-body">
          {!detail ? <div className="table-loading"><LoaderCircle className="spin" /> Loading config set…</div> : <>
            <section className="config-set-stage-picker">
              <div><strong>Active wipe stages</strong><small>The set is written into Game.ini only for the selected stages.</small></div>
              <div className="config-set-stage-options">{profiles.map((profile) => <label className={activeProfiles.has(profile.id) ? "selected" : ""} key={profile.id}><input type="checkbox" checked={activeProfiles.has(profile.id)} onChange={(event) => setActiveProfiles((current) => { const next = new Set(current); if (event.target.checked) next.add(profile.id); else next.delete(profile.id); return next; })} /><i style={{ background: profile.color }} /><span>{profile.name}<small>{profile.offsetHours ? `+${profile.offsetHours}h` : "Wipe"}</small></span></label>)}</div>
            </section>
            {hotZone ? <>
              <section className="hot-zone-settings">
                <div><strong>{hotZone.zoneName}</strong><small>Only the Ragnarok Highlands spawn region is changed. Final in-game levels are converted to ARK values automatically.</small></div>
                <div className="hot-zone-levels">
                  <label><span>Minimum wild level</span><input type="number" min="1" value={hotZone.finalMinLevel} onChange={(event) => setHotZone({ ...hotZone, finalMinLevel: Number(event.target.value) })} /></label>
                  <label><span>Maximum wild level</span><input type="number" min="1" value={hotZone.finalMaxLevel} onChange={(event) => setHotZone({ ...hotZone, finalMaxLevel: Number(event.target.value) })} /></label>
                  <label><span>ARK difficulty</span><input type="number" min="0.1" step="0.1" value={hotZone.difficulty} onChange={(event) => setHotZone({ ...hotZone, difficulty: Number(event.target.value) })} /></label>
                </div>
              </section>
              <div className="notice info wide"><ShieldCheck /><div><strong>Active bans always win</strong><span>If a dino is disabled by 24h, 72h or another active set, it is removed from this Hot Zone automatically. Its share is redistributed until the ban expires.</span></div></div>
              <section className="config-set-creature-toolbar">
                <div><strong>Hot Zone creatures</strong><small>Shares must total exactly 100%. Population cap limits how much of the entire Highlands population each dino may occupy.</small></div>
                <div><select value={newCreature} onChange={(event) => setNewCreature(event.target.value)}>{detail.creatures.filter((creature) => !hotZone.creatures.some((item) => item.className === creature.className)).map((creature) => <option value={creature.className} key={creature.className}>{creature.name}{creature.contentPackName ? ` · ${creature.contentPackName}` : ""}</option>)}</select><button className="button secondary" onClick={addCreature}><Plus /> Add dino</button></div>
              </section>
              <div className="hot-zone-total"><span>Total configured share</span><strong className={Math.abs(hotZone.creatures.reduce((sum, creature) => sum + creature.weight, 0) - 100) < 0.001 ? "valid" : "invalid"}>{Number(hotZone.creatures.reduce((sum, creature) => sum + creature.weight, 0).toFixed(3))}%</strong></div>
              <div className="config-set-creature-list">
                {hotZone.creatures.map((creature, index) => {
                  const blockers = hotZoneBlockers(creature.className);
                  return <article className="config-set-creature-card hot-zone-creature-card" key={creature.className}>
                    <header><div><strong>{creature.name}</strong><small>{blockers.length ? `Locked while active: ${blockers.join(", ")}` : "Available from wipe launch"}</small></div><button className="icon-button danger" title="Remove creature from this Hot Zone" onClick={() => setHotZone({ ...hotZone, creatures: hotZone.creatures.filter((_, target) => target !== index) })}><Trash2 /></button></header>
                    <div className="hot-zone-creature-fields">
                      <label><span>Hot Zone share</span><div><input type="number" min="0.001" max="100" step="0.1" value={creature.weight} onChange={(event) => setHotZone({ ...hotZone, creatures: hotZone.creatures.map((item, target) => target === index ? { ...item, weight: Number(event.target.value) } : item) })} /><b>%</b></div></label>
                      <label><span>Maximum Highlands population</span><div><input type="number" min="0.001" max="100" step="0.1" value={Number((creature.maxPercentage * 100).toFixed(3))} onChange={(event) => setHotZone({ ...hotZone, creatures: hotZone.creatures.map((item, target) => target === index ? { ...item, maxPercentage: Number(event.target.value) / 100 } : item) })} /><b>%</b></div></label>
                      <label><span>Final wild levels</span><strong>{hotZone.finalMinLevel}–{hotZone.finalMaxLevel}</strong></label>
                    </div>
                  </article>;
                })}
              </div>
            </> : <>
              <section className="config-set-creature-toolbar">
                <div><strong>Creature adjustments</strong><small>Add any in-game creature; technical class paths are generated automatically.</small></div>
                <div><select value={newCreature} onChange={(event) => setNewCreature(event.target.value)}>{detail.creatures.filter((creature) => !adjustments.some((item) => item.className === creature.className)).map((creature) => <option value={creature.className} key={creature.className}>{creature.name}{creature.contentPackName ? ` · ${creature.contentPackName}` : ""}</option>)}</select><button className="button secondary" onClick={addCreature}><Plus /> Add dino</button></div>
              </section>
              <div className="config-set-creature-list">
                {adjustments.map((creature, index) => <article className="config-set-creature-card" key={creature.className}>
                  <header><div><strong>{creature.name}</strong><small>{creature.contentPackName ?? "ARK"}</small></div><button className="icon-button danger" title="Remove creature from this set" onClick={() => setAdjustments((current) => current.filter((_, target) => target !== index))}><Trash2 /></button></header>
                  <div className="config-set-metric-grid">{metrics.map(([metric, label]) => {
                    const enabled = creature.configured.includes(metric);
                    return <label className={enabled ? "enabled" : ""} key={metric}><span><input type="checkbox" checked={enabled} onChange={(event) => updateAdjustment(index, { configured: event.target.checked ? [...creature.configured, metric] : creature.configured.filter((item) => item !== metric) })} />{label}</span><input type="number" min="0" max="100000" step="0.01" disabled={!enabled} value={creature[metric]} onChange={(event) => updateAdjustment(index, { [metric]: Number(event.target.value) })} /></label>;
                  })}</div>
                  <div className="config-set-restriction-grid">
                    {([ ["prohibitSpawning", "Disable spawning"], ["preventTaming", "Prevent taming"], ["prohibitTransfer", "Block transfer"], ["prohibitBreeding", "Block breeding"] ] as const).map(([key, label]) => <label key={key}><input type="checkbox" checked={creature[key]} onChange={(event) => updateAdjustment(index, { [key]: event.target.checked })} /><span>{label}</span></label>)}
                  </div>
                </article>)}
                {!adjustments.length && <div className="empty-state compact"><PawPrint /><strong>No creature adjustments</strong><span>Add a dino above to start this custom set.</span></div>}
              </div>
              {configSet.editors.some((editor) => editor.name !== "Ark.CreatureAdjustments") && <div className="notice info wide"><Braces /><div><strong>Other imported sections are preserved</strong><span>{configSet.editors.filter((editor) => editor.name !== "Ark.CreatureAdjustments").map((editor) => editor.name.replace("Ark.", "")).join(", ")} remain untouched while you edit creatures.</span></div></div>}
            </>}
          </>}
        </div>
        <div className="modal-footer"><button className="button secondary" onClick={onClose}>Cancel</button><button className="button primary" disabled={!detail || saving || Boolean(hotZone && Math.abs(hotZone.creatures.reduce((sum, creature) => sum + creature.weight, 0) - 100) >= 0.001)} onClick={async () => { setSaving(true); try { await api.saveConfigSet(configSet.id, adjustments, [...activeProfiles], hotZone); await onSaved(); } catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); } finally { setSaving(false); } }}><Save /> Save config set</button></div>
      </div>
    </div>
  );
}

function SettingsPage({ data, notify, reload }: { data: Bootstrap; notify: (message: string, tone?: "success" | "error") => void; reload: () => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [nitradoToken, setNitradoToken] = useState("");
  const [nitradoAction, setNitradoAction] = useState<"save" | "test" | "remove">();

  async function saveNitradoToken(event: FormEvent) {
    event.preventDefault();
    if (!nitradoToken.trim()) return;
    setNitradoAction("save");
    try {
      const result = await api.saveNitradoToken(nitradoToken);
      setNitradoToken("");
      await reload();
      notify(`Nitrado connected. ${result.serviceCount} service${result.serviceCount === 1 ? "" : "s"} found.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setNitradoAction(undefined);
    }
  }

  async function testNitradoConnection() {
    setNitradoAction("test");
    try {
      const result = await api.testNitrado();
      notify(`Nitrado connection works. ${result.serviceCount} service${result.serviceCount === 1 ? "" : "s"} found.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setNitradoAction(undefined);
    }
  }

  async function removeNitradoToken() {
    if (!window.confirm("Remove the saved Nitrado token from this server?")) return;
    setNitradoAction("remove");
    try {
      const result = await api.removeNitradoToken();
      await reload();
      notify(result.configured ? "Saved token removed. The environment token is now active." : "Nitrado token removed.");
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), "error");
    } finally {
      setNitradoAction(undefined);
    }
  }

  return (
    <main className="page">
      <section className="page-title-row"><div><small>PROJECT & INTEGRATIONS</small><h1>Settings</h1><p>Source files, import status and secure server-side credentials.</p></div></section>
      <section className="settings-grid">
        <div className="panel settings-card">
          <div className="settings-card-icon"><FileCog /></div>
          <div><small>PROJECT SOURCE</small><h2>{data.project.name}</h2><p>Imported from {data.beacon?.source.filename ?? "local INI files"} with {data.beacon?.configSets.length ?? 0} Beacon config sets.</p></div>
          <dl><div><dt>Game</dt><dd>ARK: Survival Evolved</dd></div><div><dt>Platform</dt><dd>{data.project.isConsole ? "Console" : "PC"}</dd></div><div><dt>Difficulty</dt><dd>{data.project.difficulty ?? 5}</dd></div><div><dt>Encrypted server data</dt><dd>{data.project.encryptedServerDataPresent ? "Preserved, not exported" : "None"}</dd></div></dl>
        </div>
        <div className="panel settings-card">
          <div className="settings-card-icon nitrado"><CloudCog /></div>
          <div className="settings-card-heading">
            <div><small>NITRADO API</small><h2>{data.integrations.nitrado.configured ? "Connected securely" : "Add your token"}</h2><p>Paste a Nitrado long-life token. It is validated before saving and is never returned to the browser.</p></div>
            <StatusPill status={data.integrations.nitrado.configured ? "active" : "not configured"} />
          </div>
          <form className="nitrado-credential-form" onSubmit={saveNitradoToken}>
            <label htmlFor="nitrado-token"><LockKeyhole /><span>{data.integrations.nitrado.configured ? "Replace token" : "Long-life token"}</span></label>
            <div className="credential-input-row">
              <input
                id="nitrado-token"
                name="nitrado-token"
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                value={nitradoToken}
                onChange={(event) => setNitradoToken(event.target.value)}
                placeholder="Paste token here"
              />
              <button className="button primary" type="submit" disabled={!nitradoToken.trim() || Boolean(nitradoAction)}>
                {nitradoAction === "save" ? <LoaderCircle className="spin" /> : <Save />} Test & save
              </button>
            </div>
          </form>
          <div className="credential-status">
            <span>
              {data.integrations.nitrado.source === "saved"
                ? "Stored securely on this server"
                : data.integrations.nitrado.source === "environment"
                  ? "Currently managed by NITRADO_TOKEN in .env"
                  : "No token is stored"}
            </span>
            <div>
              {data.integrations.nitrado.configured && (
                <button className="button secondary compact" type="button" disabled={Boolean(nitradoAction)} onClick={testNitradoConnection}>
                  {nitradoAction === "test" ? <LoaderCircle className="spin" /> : <CheckCircle2 />} Test connection
                </button>
              )}
              {data.integrations.nitrado.source === "saved" && (
                <button className="button secondary compact danger-text" type="button" disabled={Boolean(nitradoAction)} onClick={removeNitradoToken}>
                  {nitradoAction === "remove" ? <LoaderCircle className="spin" /> : <Trash2 />} Remove
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="panel settings-card span-two">
          <div className="settings-card-icon"><Upload /></div>
          <div className="grow"><small>IMPORT CONFIG</small><h2>Replace runtime source</h2><p>Upload a new Game.ini, GameUserSettings.ini or Engine.ini. The original files in this project folder remain untouched.</p></div>
          <input ref={fileRef} hidden type="file" accept=".ini" onChange={async (event) => {
            const file = event.target.files?.[0]; if (!file) return;
            try { await api.importConfig(file); await reload(); notify(`${file.name} imported.`); }
            catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); }
            event.target.value = "";
          }} />
          <button className="button secondary" onClick={() => fileRef.current?.click()}><ArrowDownToLine /> Choose INI</button>
        </div>
      </section>
      <section className="panel config-set-panel">
        <div className="panel-heading"><div><small>BEACON PROJECT INDEX</small><h2>Config sets</h2></div><span className="count-badge">{data.beacon?.configSets.length ?? 0}</span></div>
        <div className="config-set-grid">{data.beacon?.configSets.map((set) => <article key={set.id}><strong>{set.name}</strong><span>{set.editors.reduce((sum, editor) => sum + editor.count, 0).toLocaleString()} indexed objects</span><div className="chip-row">{set.editors.slice(0, 3).map((editor) => <em key={editor.name}>{editor.name.replace("Ark.", "")} · {editor.count}</em>)}</div></article>)}</div>
      </section>
    </main>
  );
}

function DeployModal({ data, profile, onClose, notify, reload }: { data: Bootstrap; profile: ConfigProfile; onClose: () => void; notify: (message: string, tone?: "success" | "error") => void; reload: () => Promise<void> }) {
  const [selected, setSelected] = useState(() => new Set(data.state.servers.filter((item) => item.enabled).map((item) => item.id)));
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.previewDeploy>>>();
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [adminPassword, setAdminPassword] = useState("");
  const [serverPasswordEnabled, setServerPasswordEnabled] = useState(false);
  const [serverPassword, setServerPassword] = useState("");
  const [startServersAfterDeploy, setStartServersAfterDeploy] = useState(true);
  const timedConfigSets = useMemo(
    () => data.beacon?.configSets.filter((configSet) => Boolean(configSet.stage)) ?? [],
    [data.beacon?.configSets]
  );
  const [activeConfigSets, setActiveConfigSets] = useState(
    () => new Set(
      profile.activeConfigSets.filter((setName) => timedConfigSets.some((set) => set.name === setName))
    )
  );
  const [credentialsLoading, setCredentialsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.entries(profile.id, "GameUserSettings.ini", { category: "", q: "ServerAdminPassword", page: 1, limit: 100 }),
      api.entries(profile.id, "GameUserSettings.ini", { category: "", q: "ServerPassword", page: 1, limit: 100 })
    ]).then(([adminResult, passwordResult]) => {
      if (cancelled) return;
      const currentAdmin = adminResult.items.find((item) => item.key.toLowerCase() === "serveradminpassword");
      const currentPassword = passwordResult.items.find((item) => item.key.toLowerCase() === "serverpassword");
      const pin = currentPassword?.value ?? "";
      setAdminPassword(currentAdmin?.value ?? "");
      setServerPassword(pin);
      setServerPasswordEnabled(Boolean(pin));
    }).catch((error) => {
      if (!cancelled) notify(error instanceof Error ? error.message : String(error), "error");
    }).finally(() => {
      if (!cancelled) setCredentialsLoading(false);
    });
    return () => { cancelled = true; };
  }, [profile.id, notify]);

  const credentials = { adminPassword, serverPasswordEnabled, serverPassword };
  const credentialsValid = Boolean(adminPassword) && (!serverPasswordEnabled || Boolean(serverPassword));

  async function generatePreview() {
    setLoading(true);
    try { setPreview(await api.previewDeploy(profile.id, [...selected], credentials, startServersAfterDeploy, [...activeConfigSets])); }
    catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); }
    finally { setLoading(false); }
  }
  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal wide-modal deploy-modal">
        <div className="modal-heading"><div><small>SAFE DEPLOYMENT</small><h2>Deploy {profile.name}</h2></div><button className="icon-button" onClick={onClose}><X /></button></div>
        <div className="modal-body">
          {!preview ? <>
            <div className="deploy-profile"><i style={{ background: profile.color }} /><div><strong>{profile.name}</strong><span>{profile.description}</span></div><b>{profile.offsetHours ? `+${profile.offsetHours}h` : "WIPE"}</b></div>
            <div className="deploy-credentials">
              <div className="deploy-credentials-heading"><LockKeyhole /><div><strong>Server access</strong><span>Applied only to the servers selected for this deployment.</span></div></div>
              <div className="deploy-credential-grid">
                <label>
                  <span>Admin password</span>
                  <input type="text" autoComplete="off" spellCheck={false} value={adminPassword} disabled={credentialsLoading} onChange={(event) => setAdminPassword(event.target.value)} placeholder={credentialsLoading ? "Loading current value…" : "Required"} />
                </label>
                <div className="deploy-pin-control">
                  <label className="switch-label"><input type="checkbox" checked={serverPasswordEnabled} disabled={credentialsLoading} onChange={(event) => setServerPasswordEnabled(event.target.checked)} /><i /><span>Require server PIN/password</span></label>
                  {serverPasswordEnabled ? <input aria-label="Server PIN or password" type="text" inputMode="numeric" autoComplete="off" spellCheck={false} value={serverPassword} onChange={(event) => setServerPassword(event.target.value)} placeholder="Enter server PIN / password" /> : <small>PIN/password will be removed from every selected server.</small>}
                </div>
              </div>
              {!credentialsLoading && !credentialsValid && <small className="deploy-credential-error">Enter an admin password{serverPasswordEnabled ? " and server PIN/password" : ""} before previewing.</small>}
            </div>
            <label className="check-row deploy-start-option"><input type="checkbox" checked={startServersAfterDeploy} onChange={(event) => setStartServersAfterDeploy(event.target.checked)} /><span><strong>Start servers after deployment</strong><small>{startServersAfterDeploy ? "Servers that are currently running will start again after their config is uploaded." : "All selected servers will remain stopped after backup and upload."}</small></span></label>
            <div className="field-block deploy-config-sets">
              <span>Timed bans for this deployment ({activeConfigSets.size}/{timedConfigSets.length})</span>
              <p className="field-help">Uncheck a set to remove those bans from every selected server in this deployment.</p>
              <div className="config-set-selector">
                {timedConfigSets.map((configSet) => (
                  <label className={activeConfigSets.has(configSet.name) ? "selected" : ""} key={configSet.id}>
                    <input
                      type="checkbox"
                      checked={activeConfigSets.has(configSet.name)}
                      onChange={(event) => setActiveConfigSets((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(configSet.name); else next.delete(configSet.name);
                        return next;
                      })}
                    />
                    <span><Check size={14} />{configSet.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="field-block"><span>Select servers ({selected.size})</span><div className="server-check-grid deploy-checks">{data.state.servers.map((server) => <label key={server.id}><input type="checkbox" disabled={!server.enabled} checked={selected.has(server.id)} onChange={(event) => setSelected((current) => { const next = new Set(current); if (event.target.checked) next.add(server.id); else next.delete(server.id); return next; })} /><span>{server.map}<small>{server.address}:{server.port}</small></span></label>)}</div></div>
            <div className="deploy-safety"><ShieldCheck /><div><strong>Nitrado backup · stop all · upload 3 at a time · restart</strong><span>No current INI files are downloaded. Each server receives its generated config and its own name.</span></div></div>
          </> : <>
            <div className="preview-heading"><CheckCircle2 /><div><strong>Preview ready</strong><span>{preview.servers.length} servers · {preview.files.reduce((sum, file) => sum + file.overrides, 0)} profile overrides</span></div></div>
            <div className="deploy-credential-summary"><LockKeyhole /><div><strong>Confirm exact deployment settings</strong><span>Admin password: <code>{adminPassword}</code> · server PIN: <code>{serverPasswordEnabled ? serverPassword : "removed"}</code> · timed bans: <code>{activeConfigSets.size ? [...activeConfigSets].join(", ") : "none"}</code> · after upload: <code>{startServersAfterDeploy ? "start servers" : "leave stopped"}</code></span></div></div>
            <div className="preview-files">{preview.files.map((file) => <article key={file.file}><FileCog /><div><strong>{file.file}</strong><span>{formatBytes(file.bytes)} · {file.perServer ? "per-server output" : "SHA-256"}</span>{!file.perServer && <code>{file.sha256.slice(0, 20)}…</code>}</div><b>{file.overrides} changes</b></article>)}</div>
            {preview.warnings.map((warning) => <div className="notice warning wide" key={warning}><AlertTriangle /><div><strong>Review needed</strong><span>{warning}</span></div></div>)}
            <label className="check-row"><input type="checkbox" checked={dryRun} onChange={(event) => { setDryRun(event.target.checked); setConfirmed(false); }} /><span><strong>Dry run only</strong><small>Validate everything without changing Nitrado.</small></span></label>
            {!dryRun && <label className="check-row critical"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span><strong>I approve a live cluster deployment</strong><small>The selected servers may be stopped and restarted.</small></span></label>}
          </>}
        </div>
        <div className="modal-footer">
          {preview && <button className="button secondary" onClick={() => setPreview(undefined)}><ChevronLeft /> Back</button>}
          <button className="button secondary" onClick={onClose}>Cancel</button>
          {!preview ? <button className="button primary" disabled={loading || credentialsLoading || !credentialsValid || !selected.size} onClick={generatePreview}>{loading || credentialsLoading ? <LoaderCircle className="spin" /> : <ShieldCheck />} Generate preview</button> :
            <button className={cx("button", dryRun ? "primary" : "danger-button")} disabled={loading || (!dryRun && (!confirmed || !data.integrations.nitrado.configured))} onClick={async () => {
              setLoading(true);
              try {
                const deployment = await api.deploy(profile.id, [...selected], dryRun, credentials, startServersAfterDeploy, [...activeConfigSets]);
                notify(`${dryRun ? "Dry run" : "Deployment"} queued: ${deployment.id}`);
                await reload(); onClose();
              } catch (error) { notify(error instanceof Error ? error.message : String(error), "error"); }
              finally { setLoading(false); }
            }}>{loading ? <LoaderCircle className="spin" /> : <Rocket />} {dryRun ? "Run validation" : "Deploy live"}</button>}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [data, setData] = useState<Bootstrap>();
  const [page, setPage] = useState<Page>("dashboard");
  const [profileId, setProfileId] = useState("launch");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "success" | "error" }>();
  const [fatalError, setFatalError] = useState<string>();
  const [needsLogin, setNeedsLogin] = useState(false);

  const load = useCallback(async () => {
    try {
      const bootstrap = await api.bootstrap();
      setData(bootstrap);
      setNeedsLogin(false);
      setFatalError(undefined);
      if (!bootstrap.state.profiles.some((profile) => profile.id === profileId)) {
        setProfileId(bootstrap.state.profiles[0]?.id ?? "launch");
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setNeedsLogin(true);
        setFatalError(undefined);
        return;
      }
      setFatalError(error instanceof Error ? error.message : String(error));
    }
  }, [profileId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!data?.state.deployments.some((deployment) => ["queued", "running"].includes(deployment.status))) return;
    const timer = setInterval(() => void load(), 2_000);
    return () => clearInterval(timer);
  }, [data?.state.deployments, load]);

  const notify = useCallback((message: string, tone: "success" | "error" = "success") => setToast({ message, tone }), []);
  const profile = useMemo(() => data?.state.profiles.find((item) => item.id === profileId) ?? data?.state.profiles[0], [data, profileId]);

  if (needsLogin) return <LoginScreen onSuccess={load} />;
  if (fatalError) return <div className="fatal"><AlertTriangle /><h1>Could not start Wazy Control</h1><p>{fatalError}</p><button className="button primary" onClick={() => void load()}>Retry</button></div>;
  if (!data || !profile) return <div className="splash"><div className="brand-mark"><Zap /></div><LoaderCircle className="spin" /><span>Indexing 47 MB of ARK config…</span></div>;

  const navigate = (target: Page) => { setPage(target); setSidebarOpen(false); };
  return (
    <div className="app-shell">
      <aside className={cx("sidebar", sidebarOpen && "open")}>
        <div className="brand"><span className="brand-mark"><Zap /></span><div><strong>WAZY</strong><small>CONTROL</small></div><button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X /></button></div>
        <nav>
          <button className={page === "dashboard" ? "active" : ""} onClick={() => navigate("dashboard")}><Gauge /><span>Overview</span></button>
          <div className="nav-label">CONFIGURATION</div>
          {data.editors.map((editor) => {
            const Icon = iconMap[editor.icon] ?? Braces;
            const count =
              editor.id === "general"
                ? data.catalogs.generalSettings
                : editor.id === "creatures"
                ? data.catalogs.creatures
                : editor.id === "loot"
                  ? data.catalogs.lootDrops
                  : data.files.reduce((sum, file) => sum + (file.categories[editor.id] ?? 0), 0);
            return <button className={page === editor.id ? "active" : ""} onClick={() => navigate(editor.id)} key={editor.id}><Icon /><span>{editor.label}</span>{editor.id !== "custom" && <b>{count}</b>}</button>;
          })}
          <button className={page === "config-sets" ? "active" : ""} onClick={() => navigate("config-sets")}><Layers3 /><span>Config Sets</span><b>{data.beacon?.configSets.length ?? 0}</b></button>
          <div className="nav-label">OPERATIONS</div>
          <button className={page === "servers" ? "active" : ""} onClick={() => navigate("servers")}><Server /><span>Servers</span><b>{data.state.servers.length}</b></button>
          <button className={page === "automation" ? "active" : ""} onClick={() => navigate("automation")}><CalendarClock /><span>Automation</span></button>
          <button className={page === "deployments" ? "active" : ""} onClick={() => navigate("deployments")}><History /><span>Deployments</span>{data.state.deployments.some((item) => item.status === "running") && <i className="nav-live" />}</button>
        </nav>
        <div className="sidebar-footer"><button className={page === "settings" ? "active" : ""} onClick={() => navigate("settings")}><Settings /><span>Settings</span></button><div className="connection-mini"><span className={data.integrations.nitrado.configured ? "online" : ""} /><div><strong>Nitrado API</strong><small>{data.integrations.nitrado.configured ? "Connected" : "Not configured"}</small></div></div></div>
      </aside>
      {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}
      <div className="workspace">
        <header className="topbar">
          <button className="mobile-menu" onClick={() => setSidebarOpen(true)}><Menu /></button>
          <div className="project-crumb"><small>PROJECT</small><strong>{data.project.name}</strong><span>ASE</span></div>
          <div className="topbar-actions">
            <label className="profile-picker"><span style={{ background: profile.color }} /><select value={profileId} onChange={(event) => setProfileId(event.target.value)}>{data.state.profiles.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
            <button className="button primary deploy-button" onClick={() => setDeployOpen(true)}><Rocket size={17} /><span>Deploy</span></button>
          </div>
        </header>
        {page === "dashboard" ? <Dashboard data={data} profile={profile} onNavigate={navigate} onDeploy={() => setDeployOpen(true)} /> :
          page === "general" ? <GeneralSettingsEditor profile={profile} notify={notify} reload={load} /> :
          page === "crafting" ? <CraftingEditor profile={profile} notify={notify} reload={load} /> :
          page === "creatures" ? <CreatureEditor profile={profile} notify={notify} /> :
          page === "spawns" ? <SpawnEditor profile={profile} notify={notify} /> :
          page === "loot" ? <LootEditor profile={profile} notify={notify} /> :
          page === "config-sets" ? <ConfigSetsPage data={data} notify={notify} reload={load} /> :
          data.editors.some((editor) => editor.id === page) ? <ConfigEditor data={data} profile={profile} category={page} notify={notify} reload={load} /> :
          page === "servers" ? <ServersPage data={data} notify={notify} reload={load} /> :
          page === "automation" ? <AutomationPage data={data} notify={notify} reload={load} /> :
          page === "deployments" ? <DeploymentsPage data={data} /> :
          <SettingsPage data={data} notify={notify} reload={load} />}
      </div>
      {deployOpen && <DeployModal data={data} profile={profile} onClose={() => setDeployOpen(false)} notify={notify} reload={load} />}
      {toast && <Toast {...toast} onClose={() => setToast(undefined)} />}
    </div>
  );
}
